import {
  AddOutlined,
  CancelOutlined,
  DescriptionOutlined,
  EditOutlined,
  ExpandMoreOutlined,
  FactCheckOutlined,
  Inventory2Outlined,
  LockOutlined,
  QrCodeScannerOutlined,
  SearchOutlined
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  InventoryCountCampaign,
  InventoryCountCampaignMutation,
  InventoryCountFinalReview,
  InventoryCountDecision,
  InventoryCountDiscrepancyCause,
  InventoryCountInvestigation,
  InventoryCountMode,
  InventoryCountScope,
  InventoryCountTask,
  WarehouseNode,
  approveInventoryCountCampaign,
  cancelInventoryCountCampaign,
  confirmInventoryCountContent,
  createInventoryCountCampaign,
  loadDeliveryClients,
  loadInventoryCountCampaigns,
  loadInventoryCountFinalReview,
  loadInventoryCountDocumentContent,
  loadInventoryCountDocuments,
  loadInventoryCountTasks,
  loadProcessPointMasterData,
  loadWarehouseStructure,
  prepareInventoryCountCampaign,
  previewInventoryCount,
  previewAbcInventoryCount,
  observeInventoryCountHu,
  observeInventoryCountContent,
  recountInventoryCountTask,
  remediateInventoryCountContent,
  removeInventoryCountObservation,
  resolveInventoryCountDiscrepancy,
  resolveInventoryCountInvestigation,
  startInventoryCountInvestigation,
  startInventoryCountTask,
  submitInventoryCountTask,
  updateInventoryCountCampaign
} from "./api";
import { operationalStatusColor, operationalStatusLabel } from "./operationalStatus";
import { PageHeader } from "./PageHeader";
import { MobileActionBar } from "./MobileActionBar";
import { MobileOperationalHeader } from "./MobileOperationalHeader";
import { ResponsiveStatusTabs } from "./ResponsiveStatusTabs";

interface Props {
  onBack: () => void;
  canManage: boolean;
  canSupervise: boolean;
  canAdjust: boolean;
  canApprove: boolean;
  canCancel: boolean;
}

type InventoryCountTab = "PREPARATION" | "TO_DO" | "IN_PROGRESS" | "COMPLETED";

function emptyDraft(warehouseId = ""): InventoryCountCampaignMutation {
  return {
    warehouseId, clientId: null, displayName: "",
    countMode: "HU_AND_CONTENT", scopeMode: "WAREHOUSE",
    selectionMethod: "GENERAL", abcClasses: ["A"],
    blindCount: true, countDeadline: null, assignedUserId: null,
    scopes: [], version: null
  };
}

export function InventoryCountPage({
  onBack, canManage, canSupervise, canAdjust, canApprove, canCancel
}: Props) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage?.startsWith("en") ? "en-GB" : "fr-FR";
  const countModes: Array<{ value: InventoryCountMode; title: string; description: string }> = (["HU_ONLY", "CONTENT_ONLY", "HU_AND_CONTENT"] as InventoryCountMode[]).map((value) => ({ value, title: t(`inventory.countModes.${value}.title`), description: t(`inventory.countModes.${value}.description`) }));
  const discrepancyCauses: Array<{ value: InventoryCountDiscrepancyCause; label: string }> = (["COUNT_ERROR", "DATA_ENTRY_ERROR", "LOSS", "DAMAGE", "UNDECLARED_CONSUMPTION", "MISPLACED_STOCK", "FOUND_SURPLUS", "PROCESS_ERROR", "OTHER"] as InventoryCountDiscrepancyCause[]).map((value) => ({ value, label: t(`inventory.causes.${value}`) }));
  const discrepancyCauseLabels = Object.fromEntries(discrepancyCauses.map((cause) => [cause.value, cause.label])) as Record<InventoryCountDiscrepancyCause, string>;
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<InventoryCountCampaign | null>(null);
  const [draft, setDraft] = useState<InventoryCountCampaignMutation>(emptyDraft());
  const [expanded, setExpanded] = useState<string | false>(false);
  const [campaignToPrepare, setCampaignToPrepare] = useState<InventoryCountCampaign | null>(null);
  const [campaignToCount, setCampaignToCount] = useState<InventoryCountCampaign | null>(null);
  const [resolutionMode, setResolutionMode] = useState(false);
  const [locationScan, setLocationScan] = useState("");
  const [huScan, setHuScan] = useState("");
  const [activeHuNumber, setActiveHuNumber] = useState<string | null>(null);
  const [contentQuantities, setContentQuantities] = useState<Record<string, string>>({});
  const [contentSerialScans, setContentSerialScans] = useState<Record<string, string>>({});
  const [blindContent, setBlindContent] = useState({
    itemCode: "", lotNumber: "", serialNumber: "", quantity: "", uom: "EA"
  });
  const [resolutionReason, setResolutionReason] = useState("");
  const [resolutionCause, setResolutionCause] = useState<InventoryCountDiscrepancyCause | "">("");
  const [taskToRecount, setTaskToRecount] = useState<InventoryCountTask | null>(null);
  const [recountComment, setRecountComment] = useState("");
  const [contentAdjustmentToConfirm, setContentAdjustmentToConfirm] = useState<{
    task: InventoryCountTask; line: InventoryCountTask["contentLines"][number];
  } | null>(null);
  const [activeTab, setActiveTab] = useState<InventoryCountTab>("TO_DO");
  const [searchQuery, setSearchQuery] = useState("");
  const [conformingHuScanWarning, setConformingHuScanWarning] = useState<string | null>(null);
  const [campaignToCancel, setCampaignToCancel] = useState<InventoryCountCampaign | null>(null);
  const [cancellationReason, setCancellationReason] = useState("");
  const [investigationToResolve, setInvestigationToResolve] = useState<InventoryCountInvestigation | null>(null);
  const [investigationDecision, setInvestigationDecision] = useState("");
  const [investigationLocation, setInvestigationLocation] = useState("");
  const [investigationReason, setInvestigationReason] = useState("");
  const [campaignToApprove, setCampaignToApprove] = useState<InventoryCountCampaign | null>(null);
  const [approvalReason, setApprovalReason] = useState("");
  const [mobileSelectedId, setMobileSelectedId] = useState<string | null>(null);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<HTMLElement | null>(null);

  const campaigns = useQuery({ queryKey: ["inventory-counts"], queryFn: loadInventoryCountCampaigns });
  const structure = useQuery({ queryKey: ["warehouse-structure"], queryFn: loadWarehouseStructure });
  const clients = useQuery({ queryKey: ["delivery-clients"], queryFn: loadDeliveryClients });
  const processPoints = useQuery({ queryKey: ["process-points"], queryFn: loadProcessPointMasterData });
  const countTasks = useQuery({
    queryKey: ["inventory-count-tasks", campaignToCount?.id],
    queryFn: () => loadInventoryCountTasks(campaignToCount!.id),
    enabled: Boolean(campaignToCount)
  });
  const currentTask = (countTasks.data ?? []).find((task) => task.status === "IN_PROGRESS")
    ?? (countTasks.data ?? []).find((task) => task.status === "READY");
  const discrepancyTasks = (countTasks.data ?? []).filter((task) => task.status === "TO_RESOLVE");
  const structureById = useMemo(() => new Map((structure.data ?? []).map((node) => [node.id, node])), [structure.data]);
  const processPointById = useMemo(() => new Map((processPoints.data ?? []).map((point) => [point.id, point])), [processPoints.data]);
  const sectionAreaById = useMemo(() => new Map((structure.data ?? [])
    .filter((node) => node.type === "STORAGE_SECTION")
    .map((node) => [node.id, node.parentId])), [structure.data]);
  const warehouses = (structure.data ?? []).filter((node) => node.type === "WAREHOUSE" && node.status === "ACTIVE");
  const selectedWarehouse = draft.warehouseId || warehouses[0]?.id || "";
  const abcPreview = useQuery({
    queryKey: ["inventory-count-abc-preview", selectedWarehouse, draft.clientId, draft.abcClasses],
    queryFn: () => previewAbcInventoryCount({
      warehouseId: selectedWarehouse, clientId: draft.clientId!, classes: draft.abcClasses,
      dueThrough: new Date().toISOString().slice(0, 10)
    }),
    enabled: dialogOpen && draft.selectionMethod === "ABC" && Boolean(selectedWarehouse)
      && Boolean(draft.clientId) && draft.abcClasses.length > 0,
    staleTime: 5_000
  });
  const effectiveScopes = draft.selectionMethod === "ABC"
    ? (abcPreview.data?.scopes ?? [])
    : draft.selectionMethod === "GENERAL" ? [] : draft.scopes;
  const scopeReady = draft.selectionMethod === "ABC"
    ? Boolean(abcPreview.data && abcPreview.data.locationCount > 0)
    : draft.scopeMode === "WAREHOUSE" || draft.scopes.length > 0;
  const previewRequest = {
    ...draft,
    warehouseId: selectedWarehouse,
    scopeMode: draft.selectionMethod === "ABC"
      ? "SELECTED_LOCATIONS" as const
      : draft.selectionMethod === "GENERAL" ? "WAREHOUSE" as const : draft.scopeMode,
    scopes: effectiveScopes
  };
  const preview = useQuery({
    queryKey: ["inventory-count-preview", previewRequest],
    queryFn: () => previewInventoryCount(previewRequest),
    enabled: dialogOpen && Boolean(selectedWarehouse) && scopeReady,
    staleTime: 5_000
  });
  const campaignGroups = useMemo(() => {
    const items = campaigns.data ?? [];
    return {
      PREPARATION: items.filter((campaign) => campaign.status === "DRAFT"),
      TO_DO: items.filter((campaign) => campaign.status === "READY"),
      IN_PROGRESS: items.filter((campaign) => ["IN_PROGRESS", "TO_RESOLVE", "PENDING_APPROVAL"].includes(campaign.status)),
      COMPLETED: items.filter((campaign) => ["COMPLETED", "CANCELLED"].includes(campaign.status))
    } satisfies Record<InventoryCountTab, InventoryCountCampaign[]>;
  }, [campaigns.data]);
  const normalizedSearchQuery = searchQuery.trim().toLocaleLowerCase(locale);
  const visibleCampaigns = useMemo(() => {
    const tabCampaigns = campaignGroups[activeTab];
    if (!normalizedSearchQuery) return tabCampaigns;
    return tabCampaigns.filter((campaign) => {
      const searchableValues = [
        campaign.campaignNumber,
        campaign.displayName,
        campaign.warehouseCode,
        campaign.warehouseName,
        campaign.clientCode,
        campaign.clientName,
        campaign.status,
        t(`status.${campaign.status}`, { defaultValue: operationalStatusLabel(campaign.status) }),
        ...campaign.locations.flatMap((location) => [location.code, location.displayName]),
        ...campaign.investigations.flatMap((investigation) => [
          investigation.actionNumber, investigation.huNumber, investigation.locationCode,
          investigation.status, investigation.reason
        ]),
        ...campaign.decisions.flatMap((decision) => [
          decision.locationCode, decision.huNumber, decision.action, decision.reason,
          ...decision.recountTargets.flatMap((target) => [
            target.huNumber, target.itemCode, target.lotNumber, target.serialNumber
          ])
        ])
      ];
      return searchableValues.some((value) =>
        String(value ?? "").toLocaleLowerCase(locale).includes(normalizedSearchQuery));
    });
  }, [activeTab, campaignGroups, normalizedSearchQuery]);
  const expandedCampaign = (campaigns.data ?? []).find((campaign) => campaign.id === expanded);
  const inventoryDocuments = useQuery({
    queryKey: ["inventory-count-documents", expanded],
    queryFn: () => loadInventoryCountDocuments(expanded as string),
    enabled: Boolean(expanded) && expandedCampaign?.status === "COMPLETED",
    refetchInterval: (query) => (query.state.data?.length ?? 0) === 0 ? 2_000 : false
  });
  const finalReview = useQuery({
    queryKey: ["inventory-count-final-review", campaignToApprove?.id],
    queryFn: () => loadInventoryCountFinalReview(campaignToApprove!.id),
    enabled: Boolean(campaignToApprove)
  });

  const openInventoryDocument = useMutation({
    mutationFn: async ({ campaignId, documentId }: { campaignId: string; documentId: string }) => {
      const preview = window.open("", "_blank");
      const blob = await loadInventoryCountDocumentContent(campaignId, documentId);
      const url = URL.createObjectURL(blob);
      if (preview) preview.location.href = url;
      else window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    }
  });

  const areas = (structure.data ?? []).filter((node) =>
    node.type === "STORAGE_AREA" && node.warehouseId === selectedWarehouse && node.status === "ACTIVE");
  const binsByArea = useMemo(() => {
    const nodes = structure.data ?? [];
    const sectionArea = new Map(nodes.filter((node) => node.type === "STORAGE_SECTION")
      .map((node) => [node.id, node.parentId]));
    const map = new Map<string, WarehouseNode[]>();
    nodes.filter((node) => node.type === "STORAGE_BIN" && node.warehouseId === selectedWarehouse && node.status === "ACTIVE")
      .forEach((bin) => {
        const areaId = bin.parentId && sectionArea.get(bin.parentId) || bin.parentId;
        if (!areaId) return;
        map.set(areaId, [...(map.get(areaId) ?? []), bin]);
      });
    return map;
  }, [structure.data, selectedWarehouse]);
  const points = (processPoints.data ?? []).filter((point) =>
    point.warehouseId === selectedWarehouse && point.status === "ACTIVE");

  const save = useMutation({
    mutationFn: () => editing
      ? updateInventoryCountCampaign(editing.id, previewRequest)
      : createInventoryCountCampaign(previewRequest),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["inventory-counts"] });
      setDialogOpen(false);
    }
  });
  const prepare = useMutation({
    mutationFn: prepareInventoryCountCampaign,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["inventory-counts"] });
      setCampaignToPrepare(null);
    }
  });
  const cancelCampaign = useMutation({
    mutationFn: () => cancelInventoryCountCampaign(campaignToCancel!, cancellationReason),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["inventory-counts"] });
      setCampaignToCancel(null);
      setCancellationReason("");
      setActiveTab("COMPLETED");
    }
  });
  const approveCampaign = useMutation({
    mutationFn: () => approveInventoryCountCampaign(campaignToApprove!, approvalReason.trim()),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["inventory-counts"] });
      await queryClient.invalidateQueries({ queryKey: ["inventory-count-documents"] });
      setCampaignToApprove(null);
      setApprovalReason("");
      setActiveTab("COMPLETED");
    }
  });
  async function refreshExecution() {
    await queryClient.invalidateQueries({ queryKey: ["inventory-count-tasks", campaignToCount?.id] });
    await queryClient.invalidateQueries({ queryKey: ["inventory-counts"] });
  }
  const startCount = useMutation({
    mutationFn: ({ task, code }: { task: InventoryCountTask; code: string }) => startInventoryCountTask(task.id, code),
    onSuccess: async () => {
      setLocationScan("");
      setActiveTab("IN_PROGRESS");
      await refreshExecution();
    }
  });
  const observeHu = useMutation({
    mutationFn: ({ task, hu }: { task: InventoryCountTask; hu: string }) => observeInventoryCountHu(task.id, hu),
    onSuccess: async (_, variables) => {
      setHuScan("");
      if (variables.task.countMode !== "HU_ONLY") {
        setActiveHuNumber(variables.hu.trim().toUpperCase());
      }
      await refreshExecution();
    }
  });
  const observeContent = useMutation({
    mutationFn: ({ task, request }: {
      task: InventoryCountTask;
      request: Parameters<typeof observeInventoryCountContent>[1];
    }) => observeInventoryCountContent(task.id, request),
    onSuccess: async () => {
      setContentSerialScans({});
      await refreshExecution();
    }
  });
  const confirmContent = useMutation({
    mutationFn: ({ task, hu }: { task: InventoryCountTask; hu: string }) =>
      confirmInventoryCountContent(task.id, hu),
    onSuccess: async () => {
      setActiveHuNumber(null);
      setContentQuantities({});
      setContentSerialScans({});
      setBlindContent({ itemCode: "", lotNumber: "", serialNumber: "", quantity: "", uom: "EA" });
      await refreshExecution();
    }
  });
  const submitCount = useMutation({
    mutationFn: ({ task, empty }: { task: InventoryCountTask; empty: boolean }) => submitInventoryCountTask(task, empty),
    onSuccess: async () => { await refreshExecution(); }
  });
  const recount = useMutation({
    mutationFn: ({ task, comment }: { task: InventoryCountTask; comment: string }) =>
      recountInventoryCountTask(task.id, comment),
    onSuccess: async () => {
      setTaskToRecount(null);
      setRecountComment("");
      setCampaignToCount(null);
      setResolutionMode(false);
      setActiveTab("TO_DO");
      await refreshExecution();
    }
  });
  const removeObservation = useMutation({
    mutationFn: ({ task, hu }: { task: InventoryCountTask; hu: string }) =>
      removeInventoryCountObservation(
        task.id, hu, resolutionCause as InventoryCountDiscrepancyCause, resolutionReason
      ),
    onSuccess: async () => { setResolutionReason(""); setResolutionCause(""); await refreshExecution(); }
  });
  const resolveDiscrepancy = useMutation({
    mutationFn: ({ task, hu, decision }: {
      task: InventoryCountTask; hu: string;
      decision: "SEARCH_MISSING" | "INVESTIGATE_UNKNOWN" | "REGULARIZE_LOCATION";
    }) => resolveInventoryCountDiscrepancy(
      task.id, hu, decision, resolutionCause as InventoryCountDiscrepancyCause, resolutionReason
    ),
    onSuccess: async () => { setResolutionReason(""); setResolutionCause(""); await refreshExecution(); }
  });
  const remediateContent = useMutation({
    mutationFn: ({ task, snapshotId, decision }: {
      task: InventoryCountTask; snapshotId: string;
      decision: "OBSERVATION_CORRECTED" | "STOCK_ADJUSTED" | "INVESTIGATION_OPENED";
    }) => remediateInventoryCountContent(
      task.id, snapshotId, decision, resolutionCause as InventoryCountDiscrepancyCause, resolutionReason
    ),
    onSuccess: async () => {
      setResolutionReason("");
      setResolutionCause("");
      await refreshExecution();
    }
  });
  const startInvestigation = useMutation({
    mutationFn: startInventoryCountInvestigation,
    onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: ["inventory-counts"] }); }
  });
  const resolveInvestigation = useMutation({
    mutationFn: () => resolveInventoryCountInvestigation(
      investigationToResolve!.id,
      investigationDecision as NonNullable<InventoryCountInvestigation["resolutionDecision"]>,
      investigationDecision === "FOUND_ELSEWHERE" ? investigationLocation.trim() : null,
      investigationReason.trim()
    ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["inventory-counts"] });
      setInvestigationToResolve(null);
      setInvestigationDecision("");
      setInvestigationLocation("");
      setInvestigationReason("");
    }
  });

  function openNew() {
    setEditing(null);
    setDraft(emptyDraft(warehouses[0]?.id));
    setDialogOpen(true);
  }
  function openEdit(campaign: InventoryCountCampaign) {
    setEditing(campaign);
    setDraft({
      warehouseId: campaign.warehouseId, clientId: campaign.clientId,
      displayName: campaign.displayName, countMode: campaign.countMode,
      scopeMode: campaign.scopeMode, blindCount: campaign.blindCount,
      selectionMethod: campaign.selectionMethod, abcClasses: campaign.abcClasses,
      countDeadline: campaign.countDeadline, assignedUserId: null,
      scopes: campaign.scopes, version: campaign.version
    });
    setDialogOpen(true);
  }
  function toggleScope(scope: InventoryCountScope) {
    const exists = draft.scopes.some((candidate) =>
      candidate.locationType === scope.locationType && candidate.locationId === scope.locationId);
    setDraft({ ...draft, scopes: exists
      ? draft.scopes.filter((candidate) => candidate.locationType !== scope.locationType || candidate.locationId !== scope.locationId)
      : [...draft.scopes, scope] });
  }
  function selected(type: InventoryCountScope["locationType"], id: string) {
    return draft.scopes.some((scope) => scope.locationType === type && scope.locationId === id);
  }
  function scopeLabel(scope: InventoryCountScope) {
    if (scope.locationType === "PROCESS_POINT") {
      const point = processPointById.get(scope.locationId);
      return point ? t("inventory.scope.process", { code: point.code, name: point.displayName }) : t("inventory.scope.unknownProcess");
    }
    const node = structureById.get(scope.locationId);
    if (!node) return scope.locationType === "STORAGE_AREA" ? t("inventory.scope.unknownArea") : t("inventory.scope.unknownLocation");
    return scope.locationType === "STORAGE_AREA"
      ? t("inventory.scope.area", { code: node.code, name: node.displayName })
      : t("inventory.scope.location", { code: node.code, name: node.displayName && node.displayName !== node.code ? ` · ${node.displayName}` : "" });
  }
  function draftLocationCodes(campaign: InventoryCountCampaign) {
    const bins = (structure.data ?? []).filter((node) =>
      node.type === "STORAGE_BIN" && node.warehouseId === campaign.warehouseId && node.status === "ACTIVE");
    const campaignPoints = (processPoints.data ?? []).filter((point) =>
      point.warehouseId === campaign.warehouseId && point.status === "ACTIVE");
    if (campaign.scopeMode === "WAREHOUSE") {
      return [...bins.map((bin) => bin.code), ...campaignPoints.map((point) => point.code)];
    }
    const codes = campaign.scopes.flatMap((scope) => {
      if (scope.locationType === "STORAGE_BIN") return structureById.get(scope.locationId)?.code ?? [];
      if (scope.locationType === "PROCESS_POINT") return processPointById.get(scope.locationId)?.code ?? [];
      return bins.filter((bin) => {
        const areaId = bin.parentId && (sectionAreaById.get(bin.parentId) ?? bin.parentId);
        return areaId === scope.locationId;
      }).map((bin) => bin.code);
    });
    return [...new Set(codes)];
  }

  function activeInvestigations(campaign: InventoryCountCampaign) {
    return campaign.investigations.filter((item) => ["OPEN", "IN_PROGRESS"].includes(item.status));
  }

  function investigationSummary(investigations: InventoryCountInvestigation[]) {
    const first = investigations[0];
    if (!first) return "";
    const action = first.investigationType === "MISSING_HU"
      ? t("inventory.investigation.missing")
      : t("inventory.investigation.unknown");
    return `${action} · ${first.huNumber}${investigations.length > 1 ? ` · +${investigations.length - 1}` : ""}`;
  }

  function latestRecount(campaign: InventoryCountCampaign) {
    return [...campaign.decisions].reverse().find((decision) => decision.action === "RECOUNT_REQUESTED");
  }

  function recountTargetLabel(target: InventoryCountDecision["recountTargets"][number]) {
    const traceability = target.serialNumber
      ? t("inventory.extra.serialValue", { value: target.serialNumber })
      : target.lotNumber ? t("inventory.extra.lotValue", { value: target.lotNumber }) : null;
    return [target.huNumber, target.itemCode, traceability].filter(Boolean).join(" · ");
  }

  const activeRecountDecision = campaignToCount ? latestRecount(campaignToCount) : undefined;
  const currentRecountTargets = activeRecountDecision?.locationCode === currentTask?.locationCode
    ? (activeRecountDecision?.recountTargets ?? [])
    : [];
  const currentRecountHuNumbers = [...new Set(currentRecountTargets.map((target) => target.huNumber))];
  const currentRecountHuSet = new Set(currentRecountHuNumbers);
  const currentConformingHuNumbers = currentRecountTargets.length > 0
    ? (currentTask?.expectedHuNumbers ?? []).filter((hu) => !currentRecountHuSet.has(hu))
    : [];
  const currentConformingHuSet = new Set(currentConformingHuNumbers);
  const isTargetedRecount = Boolean(currentTask && currentRecountTargets.length > 0);

  function isRecountHuCompleted(hu: string) {
    if (!currentTask) return false;
    const targets = currentRecountTargets.filter((target) => target.huNumber === hu);
    const needsContentCheck = targets.some((target) => target.targetType === "CONTENT");
    return needsContentCheck
      ? currentTask.contentConfirmedHuNumbers.includes(hu)
      : currentTask.observedHuNumbers.includes(hu);
  }

  function submitHuScan(task: InventoryCountTask, rawHu: string, force = false) {
    const hu = rawHu.trim().toUpperCase();
    if (!hu) return;
    if (!force && isTargetedRecount && currentConformingHuSet.has(hu)) {
      setConformingHuScanWarning(hu);
      return;
    }
    setConformingHuScanWarning(null);
    observeHu.mutate({ task, hu });
  }
  useEffect(() => setMobileSelectedId(null), [activeTab]);
  const selectedMobileCampaign = visibleCampaigns.find((campaign) =>
    campaign.id === mobileSelectedId) ?? null;
  const selectedHasInvestigations = selectedMobileCampaign
    ? activeInvestigations(selectedMobileCampaign).length > 0 : false;
  const mobilePrimaryLabel = selectedMobileCampaign?.status === "DRAFT"
    ? t("inventory.actions.prepare")
    : selectedMobileCampaign?.status === "READY" ? t("inventory.actions.start")
      : selectedMobileCampaign?.status === "IN_PROGRESS" ? t("inventory.actions.continue")
        : selectedMobileCampaign?.status === "TO_RESOLVE"
          && !selectedHasInvestigations ? t("inventory.actions.remediate")
          : selectedMobileCampaign?.status === "PENDING_APPROVAL"
            ? t("inventory.actions.approve")
            : selectedMobileCampaign ? t("inventory.actions.view")
              : t("inventory.actions.select");
  const mobilePrimaryAvailable = Boolean(selectedMobileCampaign)
    && (selectedMobileCampaign?.status !== "DRAFT" || canManage)
    && (selectedMobileCampaign?.status !== "TO_RESOLVE"
      || selectedHasInvestigations || canSupervise || canAdjust)
    && (selectedMobileCampaign?.status !== "PENDING_APPROVAL" || canApprove);
  const executeMobilePrimary = () => {
    if (!selectedMobileCampaign) return;
    if (selectedMobileCampaign.status === "DRAFT" && canManage) {
      setCampaignToPrepare(selectedMobileCampaign);
    } else if (["READY", "IN_PROGRESS"].includes(selectedMobileCampaign.status)) {
      setResolutionMode(false);
      setCampaignToCount(selectedMobileCampaign);
    } else if (selectedMobileCampaign.status === "TO_RESOLVE"
      && !selectedHasInvestigations && (canSupervise || canAdjust)) {
      setResolutionMode(true);
      setResolutionReason("");
      setCampaignToCount(selectedMobileCampaign);
    } else if (selectedMobileCampaign.status === "PENDING_APPROVAL" && canApprove) {
      setCampaignToApprove(selectedMobileCampaign);
      setApprovalReason("");
    } else {
      setExpanded(selectedMobileCampaign.id);
    }
  };
  const statusTabs = [
    { value: "PREPARATION" as const, label: t("inventory.tabs.preparation"),
      mobileLabel: t("inventory.tabs.preparation").toLocaleUpperCase(locale), count: campaignGroups.PREPARATION.length },
    { value: "TO_DO" as const, label: t("inventory.tabs.todo"), mobileLabel: t("inventory.tabs.todo").toLocaleUpperCase(locale),
      count: campaignGroups.TO_DO.length },
    { value: "IN_PROGRESS" as const, label: t("inventory.tabs.progress"), mobileLabel: t("inventory.tabs.progress").toLocaleUpperCase(locale),
      count: campaignGroups.IN_PROGRESS.length },
    { value: "COMPLETED" as const, label: t("inventory.tabs.completed"), mobileLabel: t("inventory.tabs.completed").toLocaleUpperCase(locale),
      count: campaignGroups.COMPLETED.length }
  ];

  return <Box sx={{
    height: { xs: "100%", sm: "auto", md: "100%" }, minHeight: 0,
    display: "flex", flexDirection: "column",
    overflow: { xs: "hidden", sm: "visible", md: "hidden" }
  }}>
    <Box sx={{ flexShrink: 0 }}>
    <MobileOperationalHeader moduleLabel={t("inventory.module")}
      search={searchQuery} onSearchChange={(value) => {
        setSearchQuery(value); setExpanded(false);
      }} searchPlaceholder={t("inventory.searchPlaceholder")} onBack={onBack} />
    <Box sx={{ display: { xs: "none", sm: "block" } }}>
    <PageHeader title={t("inventory.title")} compact onBack={onBack}
      description={t("inventory.description")}
      actions={canManage ? <Button variant="contained" startIcon={<AddOutlined />} onClick={openNew}>{t("inventory.actions.new")}</Button> : undefined} />
    </Box>
    <ResponsiveStatusTabs value={activeTab} items={statusTabs}
      onChange={(value) => { setActiveTab(value); setExpanded(false); }} />
    <TextField fullWidth size="small" value={searchQuery}
      onChange={(event) => { setSearchQuery(event.target.value); setExpanded(false); }}
      placeholder={t("inventory.searchLong")}
      inputProps={{ "aria-label": t("inventory.searchAria") }}
      InputProps={{ startAdornment: <InputAdornment position="start"><SearchOutlined /></InputAdornment> }}
      sx={{ mt: 1.5, mb: 1.5, bgcolor: "background.paper",
        display: { xs: "none", sm: "flex" } }} />
    </Box>
    <Stack spacing={1.5} sx={{
      flex: { xs: "1 1 0", sm: "none", md: 1 }, minHeight: 0,
      overflowY: { xs: "auto", sm: "visible", md: "auto" },
      pr: 0.5, pb: { xs: 11, md: 1 }
    }}>
      {campaigns.isError && <Alert severity="error">{t("inventory.errors.load")}</Alert>}
      {(campaigns.data ?? []).length === 0 && !campaigns.isLoading &&
        <Alert severity="info">{t("inventory.errors.empty")}</Alert>}
      {(campaigns.data ?? []).length > 0 && visibleCampaigns.length === 0 && !campaigns.isLoading &&
        <Alert severity="info">{normalizedSearchQuery
          ? t("inventory.errors.noMatch")
          : t("inventory.errors.noState")}</Alert>}
      {visibleCampaigns.map((campaign) => <Accordion key={campaign.id}
        expanded={expanded === campaign.id}
        sx={{
          position: "relative",
          ...(activeInvestigations(campaign).length > 0 || latestRecount(campaign)
            ? { borderLeft: 4, borderLeftColor: "warning.main" } : {}),
          outline: { xs: mobileSelectedId === campaign.id
            ? "2px solid" : "none", sm: "none" },
          outlineColor: "primary.main"
        }}
        onChange={(_, open) => {
          setMobileSelectedId(campaign.id);
          setExpanded(open ? campaign.id : false);
        }}>
        <AccordionSummary expandIcon={<ExpandMoreOutlined />} sx={{
          pb: { xs: 5, md: 1.5 },
          "& .MuiAccordionSummary-expandIconWrapper": {
            position: { xs: "absolute", md: "static" },
            left: { xs: "calc(50% - 20px)", md: "auto" },
            bottom: { xs: 4, md: "auto" },
            width: { xs: 40, md: "auto" },
            height: { xs: 40, md: "auto" },
            alignItems: "center",
            justifyContent: "center"
          }
        }}>
          <Stack direction={{ xs: "column", md: "row" }} alignItems={{ md: "center" }} spacing={2} sx={{ width: "100%", pr: 1 }}>
            <Stack direction="row" alignItems="center"
              justifyContent="space-between"
              sx={{ display: { xs: "flex", md: "none" }, width: "100%" }}>
              <Inventory2Outlined color="secondary" />
              <Chip color={operationalStatusColor(campaign.status)}
                label={activeInvestigations(campaign).length > 0
                  ? activeInvestigations(campaign).some(
                    (item) => item.status === "IN_PROGRESS")
                    ? t("inventory.status.followProgress")
                    : t("inventory.status.followRequired")
                  : t(`status.${campaign.status}`, { defaultValue: operationalStatusLabel(campaign.status) })} />
            </Stack>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography fontWeight={800}>{campaign.campaignNumber} · {campaign.displayName}</Typography>
              <Typography color="text.secondary" variant="body2">
                {campaign.warehouseCode} · {campaign.clientName ? t("inventory.card.client", { name: campaign.clientName }) : t("inventory.card.allClients")}
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 0.75 }}>
                <Chip size="small" variant="outlined" color={campaign.selectionMethod === "ABC" ? "secondary" : "default"}
                  label={campaign.selectionMethod === "ABC"
                    ? `${t("inventory.selection.ABC.title")} · ${campaign.abcClasses.join("/")}`
                    : t(`inventory.selection.${campaign.selectionMethod}.title`)} />
                <Chip size="small" color="info" variant="outlined"
                  label={countModes.find((mode) => mode.value === campaign.countMode)?.title ?? campaign.countMode} />
                <Chip size="small" variant="outlined" label={campaign.status === "DRAFT"
                  ? t("inventory.card.plannedLocations", { count: draftLocationCodes(campaign).length })
                  : t("inventory.card.locations", { count: campaign.locationCount })} />
                {campaign.status !== "DRAFT" && <Chip size="small" variant="outlined"
                  label={`${campaign.handlingUnitCount} HU`} />}
                {campaign.status !== "DRAFT" && campaign.countMode !== "HU_ONLY" && <Chip size="small" variant="outlined"
                  label={t("inventory.card.contentLines", { count: campaign.contentLineCount })} />}
                {campaign.huDiscrepancyCount > 0 && <Chip size="small" color="warning"
                  label={t("inventory.card.huDiscrepancies", { count: campaign.huDiscrepancyCount })} />}
                {campaign.contentDiscrepancyCount > 0 && <Chip size="small" color="warning"
                  label={t("inventory.card.contentDiscrepancies", { count: campaign.contentDiscrepancyCount })} />}
                {latestRecount(campaign) && <Chip size="small" color="warning" label={t("inventory.decisions.RECOUNT_REQUESTED")} />}
              </Stack>
              {latestRecount(campaign) && <Typography variant="body2" fontWeight={700}
                color="warning.dark" sx={{ mt: 0.75 }}>
                {latestRecount(campaign)?.locationCode} · {(latestRecount(campaign)?.recountTargets?.length ?? 0) > 0
                  ? t("inventory.card.elementsToCheck", { count: latestRecount(campaign)?.recountTargets?.length })
                  : t("inventory.card.locationToRecount")}
              </Typography>}
              {activeInvestigations(campaign).length > 0 && <Stack direction="row" spacing={1}
                alignItems="center" flexWrap="wrap" sx={{ mt: 0.75 }}>
                <Chip size="small" color="warning"
                  label={activeInvestigations(campaign).some((item) => item.status === "IN_PROGRESS")
                    ? t("inventory.status.actionProgress")
                    : t("inventory.status.actionRequired")} />
                <Typography variant="body2" fontWeight={700} color="warning.dark">
                  {investigationSummary(activeInvestigations(campaign))}
                </Typography>
              </Stack>}
            </Box>
            {campaign.status === "DRAFT" && canManage && <Button variant="outlined" startIcon={<EditOutlined />}
              sx={{ display: { xs: "none", sm: "inline-flex" } }}
              onClick={(event) => { event.stopPropagation(); openEdit(campaign); }}>{t("inventory.actions.edit")}</Button>}
            {campaign.status === "DRAFT" && canManage && <Button variant="contained" startIcon={<FactCheckOutlined />}
              sx={{ display: { xs: "none", sm: "inline-flex" } }}
              onClick={(event) => { event.stopPropagation(); setCampaignToPrepare(campaign); }}>{t("inventory.actions.prepare")}</Button>}
            {["READY", "IN_PROGRESS"].includes(campaign.status) && <Button variant="contained" startIcon={<QrCodeScannerOutlined />}
              sx={{ display: { xs: "none", sm: "inline-flex" } }}
              onClick={(event) => { event.stopPropagation(); setResolutionMode(false); setCampaignToCount(campaign); }}>
              {campaign.status === "READY" ? t("inventory.actions.start") : t("inventory.actions.continue")}
            </Button>}
            {campaign.status === "TO_RESOLVE" && activeInvestigations(campaign).length === 0
              && (canSupervise || canAdjust) &&
              <Button variant="contained" startIcon={<FactCheckOutlined />}
              sx={{ display: { xs: "none", sm: "inline-flex" } }}
              color={campaign.status === "TO_RESOLVE" ? "warning" : "primary"}
              onClick={(event) => { event.stopPropagation(); setResolutionMode(true); setResolutionReason(""); setCampaignToCount(campaign); }}>
              {t("inventory.actions.remediate")}
            </Button>}
            {campaign.status === "PENDING_APPROVAL" && canApprove &&
              <Button variant="contained" startIcon={<FactCheckOutlined />} color="warning"
                sx={{ display: { xs: "none", sm: "inline-flex" } }}
                onClick={(event) => {
                  event.stopPropagation();
                  setCampaignToApprove(campaign);
                  setApprovalReason("");
                }}>
                {t("inventory.actions.approve")}
              </Button>}
            {canCancel && !["COMPLETED", "CANCELLED"].includes(campaign.status) &&
              <Button color="error" startIcon={<CancelOutlined />}
                sx={{ display: { xs: "none", sm: "inline-flex" } }}
                onClick={(event) => {
                  event.stopPropagation();
                  setCampaignToCancel(campaign);
                  setCancellationReason("");
                }}>{t("inventory.actions.cancel")}</Button>}
            <Chip color={operationalStatusColor(campaign.status)}
              sx={{
                display: { xs: "none", md: "inline-flex" }
              }}
              label={activeInvestigations(campaign).length > 0
                ? activeInvestigations(campaign).some((item) => item.status === "IN_PROGRESS")
                  ? t("inventory.status.followProgress")
                  : t("inventory.status.followRequired")
                : t(`status.${campaign.status}`, { defaultValue: operationalStatusLabel(campaign.status) })} />
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}><Typography variant="caption" color="text.secondary">{t("inventory.card.control")}</Typography><Typography>{countModes.find((mode) => mode.value === campaign.countMode)?.title}</Typography></Grid>
            <Grid size={{ xs: 12, sm: 4 }}><Typography variant="caption" color="text.secondary">{t("inventory.card.method")}</Typography><Typography>{campaign.blindCount ? t("inventory.card.blind") : t("inventory.card.visible")}</Typography></Grid>
            <Grid size={{ xs: 12, sm: 4 }}><Typography variant="caption" color="text.secondary">{t("inventory.card.deadline")}</Typography><Typography>{campaign.countDeadline ? new Date(campaign.countDeadline).toLocaleDateString(locale) : t("inventory.card.undefined")}</Typography></Grid>
          </Grid>
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">{t("inventory.card.areas")}</Typography>
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 0.75 }}>
              {campaign.locations.length > 0
                ? campaign.locations.map((location) => <Chip key={`${location.locationType}-${location.locationId}`} variant="outlined" label={location.code} />)
                : draftLocationCodes(campaign).length > 0
                  ? draftLocationCodes(campaign).map((code) => <Chip key={code} variant="outlined" label={code} />)
                  : campaign.scopeMode === "WAREHOUSE"
                    ? <Chip variant="outlined" label={t("inventory.card.entireWarehouse", { warehouse: campaign.warehouseCode })} />
                    : campaign.scopes.map((scope) => <Chip key={`${scope.locationType}-${scope.locationId}`} variant="outlined" label={scopeLabel(scope)} />)}
            </Stack>
          </Box>
          {latestRecount(campaign) && <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography fontWeight={800}>{t("inventory.recount.instruction")}</Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {t("inventory.recount.location")} <strong>{latestRecount(campaign)?.locationCode}</strong>
            </Typography>
            <Typography variant="body2">
              {t("inventory.recount.supervisorComment")} {latestRecount(campaign)?.reason?.trim() || t("inventory.recount.noComment")}
            </Typography>
            {(latestRecount(campaign)?.recountTargets?.length ?? 0) > 0 && <Box sx={{ mt: 1.25 }}>
              <Typography variant="body2" fontWeight={750}>{t("inventory.recount.initial")}</Typography>
              <Stack spacing={0.75} sx={{ mt: 0.75 }}>
                {latestRecount(campaign)?.recountTargets?.map((target, index) => <Box
                  key={`${target.targetType}-${target.huNumber}-${target.itemCode ?? ""}-${index}`}
                  sx={{ p: 1, bgcolor: "background.paper", border: 1, borderColor: "warning.light", borderRadius: 1 }}>
                  <Typography variant="body2" fontWeight={700}>{recountTargetLabel(target)}</Typography>
                  {target.targetType === "CONTENT" && <Typography variant="body2" color="text.secondary">
                    {t("inventory.recount.expectedObserved", { expected: target.expectedQuantity ?? "—", observed: target.observedQuantity ?? "—", uom: target.uom ?? "" })}
                  </Typography>}
                  {target.targetType === "HU" && <Typography variant="body2" color="text.secondary">
                    {t("inventory.recount.huConfirm")}
                  </Typography>}
                </Box>)}
              </Stack>
            </Box>}
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              {t("inventory.recount.fullControl")}
            </Typography>
          </Alert>}
          {campaign.status === "CANCELLED" && <Alert severity="error" sx={{ mt: 2 }}>
            {t("inventory.result.cancelled")}{campaign.cancelledAt ? ` · ${new Date(campaign.cancelledAt).toLocaleString(locale)}` : ""}
            {campaign.cancelledBy ? ` · ${t("inventory.result.by", { name: campaign.cancelledBy })}` : ""}. {t("inventory.result.reason", { reason: campaign.cancellationReason ?? t("inventory.result.unspecified") })}
          </Alert>}
          {campaign.status === "COMPLETED" && <Box sx={{ mt: 2 }}>
            <Alert severity={campaign.decisions.length > 0 ? "warning" : "success"}>
              <Typography fontWeight={800}>
                {campaign.decisions.length > 0
                  ? t("inventory.result.treated")
                  : t("inventory.result.compliant")}
              </Typography>
              <Typography variant="body2">
                {t("inventory.result.closedLocations", { completed: campaign.completedLocationCount, total: campaign.locationCount })}
                {campaign.discrepancyLocationCount > 0
                  ? ` · ${t("inventory.result.discrepancyLocations", { count: campaign.discrepancyLocationCount })}`
                  : ` · ${t("inventory.result.noDiscrepancy")}`}
                {campaign.completedAt ? ` · ${t("inventory.result.completedAt", { date: new Date(campaign.completedAt).toLocaleString(locale) })}` : ""}
                {campaign.completedBy ? ` ${t("inventory.result.by", { name: campaign.completedBy })}` : ""}.
              </Typography>
            </Alert>
            {campaign.id === expanded && <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }} sx={{ mt: 1.5 }}>
              {(inventoryDocuments.data ?? []).map((document) => <Button key={document.id}
                variant="outlined" startIcon={<DescriptionOutlined />}
                disabled={openInventoryDocument.isPending}
                onClick={() => openInventoryDocument.mutate({ campaignId: campaign.id, documentId: document.id })}>
                {t("inventory.result.report")}
              </Button>)}
              {!inventoryDocuments.isError && inventoryDocuments.isLoading &&
                <Typography variant="body2" color="text.secondary">{t("inventory.result.loadingReport")}</Typography>}
              {!inventoryDocuments.isError && !inventoryDocuments.isLoading &&
                (inventoryDocuments.data?.length ?? 0) === 0 &&
                <Typography variant="body2" color="text.secondary">{t("inventory.result.generatingReport")}</Typography>}
              {inventoryDocuments.isError &&
                <Typography variant="body2" color="error">{t("inventory.result.reportUnavailable")}</Typography>}
            </Stack>}
          </Box>}
          {campaign.investigations.length > 0 && <Box sx={{ mt: 2 }}>
            <Typography fontWeight={800} sx={{ mb: 1 }}>
              {t("inventory.investigation.actions", { count: campaign.investigations.filter((item) => ["OPEN", "IN_PROGRESS"].includes(item.status)).length })}
            </Typography>
            <Stack spacing={1}>
              {campaign.investigations.map((investigation) => <Box key={investigation.id}
                sx={{ p: 1.5, border: 1, borderColor: "divider", borderRadius: 1.5 }}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} alignItems={{ md: "center" }}>
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Chip size="small" variant="outlined" label={investigation.actionNumber} />
                      <Typography fontWeight={750}>
                        {investigation.investigationType === "MISSING_HU" ? t("inventory.investigation.missing") : t("inventory.investigation.unknown")}
                      </Typography>
                      <Chip size="small" label={investigation.huNumber} />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {t("inventory.investigation.opened", { location: investigation.locationCode, date: new Date(investigation.createdAt).toLocaleString(locale), user: investigation.createdBy })}
                    </Typography>
                    {investigation.resolutionDecision && <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {t("inventory.investigation.finalDecision", { decision: t(`inventory.investigationDecisions.${investigation.resolutionDecision}`, { defaultValue: investigation.resolutionDecision }) })}
                      {investigation.resolutionLocationCode ? ` · ${investigation.resolutionLocationCode}` : ""}
                    </Typography>}
                  </Box>
                  {investigation.status === "OPEN" && canSupervise && <Button variant="outlined"
                    disabled={startInvestigation.isPending}
                    onClick={() => startInvestigation.mutate(investigation.id)}>{t("inventory.actions.takeCharge")}</Button>}
                  {["OPEN", "IN_PROGRESS"].includes(investigation.status) && canSupervise && <Button variant="contained" color="warning"
                    onClick={() => {
                      setInvestigationToResolve(investigation);
                      setInvestigationDecision("");
                      setInvestigationLocation("");
                      setInvestigationReason("");
                    }}>{t("inventory.actions.remediate")}</Button>}
                  <Chip size="small"
                    color={investigation.status === "RESOLVED" ? "success" : investigation.status === "IN_PROGRESS" ? "info" : "warning"}
                    label={investigation.status === "RESOLVED" ? t("inventory.status.resolved") : investigation.status === "IN_PROGRESS" ? t("inventory.tabs.progress") : t("inventory.status.todo")} />
                </Stack>
                {investigation.resolutionComment && <Typography variant="body2" sx={{ mt: 0.75 }}>
                  {t("inventory.investigation.finalReason", { reason: investigation.resolutionComment })}
                </Typography>}
              </Box>)}
            </Stack>
          </Box>}
          {["COMPLETED", "CANCELLED"].includes(campaign.status) && campaign.decisions.length > 0 &&
            <Box sx={{ mt: 2 }}>
              <Typography fontWeight={800} sx={{ mb: 1 }}>{t("inventory.investigation.trace", { count: campaign.decisions.length })}</Typography>
              <Stack spacing={1}>
                {campaign.decisions.map((decision, index) => <Box key={`${decision.performedAt}-${index}`}
                  sx={{ p: 1.5, border: 1, borderColor: "divider", borderRadius: 1.5 }}>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }}>
                    <Chip size="small" variant="outlined" label={decision.locationCode} />
                    <Typography fontWeight={700}>{t(`inventory.decisions.${decision.action}`, { defaultValue: decision.action })}</Typography>
                    {decision.huNumber && <Chip size="small" label={decision.huNumber} />}
                    <Typography variant="body2" color="text.secondary" sx={{ ml: { md: "auto" } }}>
                      {new Date(decision.performedAt).toLocaleString(locale)} · {decision.performedBy}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ mt: 0.75 }}>{t("inventory.dialogs.reason", { reason: decision.reason })}</Typography>
                  {decision.causeCode && <Typography variant="body2" color="text.secondary">
                    {t("inventory.investigation.cause", { cause: discrepancyCauseLabels[decision.causeCode] ?? decision.causeCode })}
                  </Typography>}
                </Box>)}
              </Stack>
            </Box>}
        </AccordionDetails>
      </Accordion>)}
    </Stack>

    <MobileActionBar secondaryIcon={<AddOutlined />}
      secondaryLabel={t("inventory.actions.new")} secondaryDisplayLabel={t("inventory.actions.newShort")}
      onSecondary={canManage ? openNew : undefined}
      primaryLabel={mobilePrimaryLabel}
      primaryDisabled={!mobilePrimaryAvailable}
      onPrimary={executeMobilePrimary}
      onMore={selectedMobileCampaign
        && ((selectedMobileCampaign.status === "DRAFT" && canManage)
          || (canCancel && !["COMPLETED", "CANCELLED"]
            .includes(selectedMobileCampaign.status)))
        ? setMobileMenuAnchor : undefined} />
    <Menu anchorEl={mobileMenuAnchor} open={Boolean(mobileMenuAnchor)}
      onClose={() => setMobileMenuAnchor(null)}>
      {selectedMobileCampaign?.status === "DRAFT" && canManage && (
        <MenuItem onClick={() => {
          setMobileMenuAnchor(null);
          openEdit(selectedMobileCampaign);
        }}>
          <EditOutlined sx={{ mr: 1 }} /> {t("inventory.actions.edit")}
        </MenuItem>
      )}
      {selectedMobileCampaign && canCancel
        && !["COMPLETED", "CANCELLED"].includes(selectedMobileCampaign.status) && (
        <MenuItem sx={{ color: "error.main" }} onClick={() => {
          setMobileMenuAnchor(null);
          setCampaignToCancel(selectedMobileCampaign);
          setCancellationReason("");
        }}>
          <CancelOutlined sx={{ mr: 1 }} /> {t("inventory.actions.cancel")}
        </MenuItem>
      )}
    </Menu>

    <Dialog open={Boolean(investigationToResolve)}
      onClose={() => !resolveInvestigation.isPending && setInvestigationToResolve(null)} fullWidth maxWidth="sm">
      <DialogTitle>{t("inventory.dialogs.investigationTitle", { action: investigationToResolve?.actionNumber })}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info">
            {t("inventory.dialogs.investigationInfo", { hu: investigationToResolve?.huNumber, location: investigationToResolve?.locationCode })}
          </Alert>
          <RadioGroup value={investigationDecision} onChange={(event) => setInvestigationDecision(event.target.value)}>
            {investigationToResolve?.investigationType === "MISSING_HU" ? <>
              <FormControlLabel value="FOUND_AT_EXPECTED" control={<Radio />}
                label={t("inventory.investigationDecisions.FOUND_AT_EXPECTED")} />
              <FormControlLabel value="FOUND_ELSEWHERE" control={<Radio />}
                label={t("inventory.investigationDecisions.FOUND_ELSEWHERE")} />
              <FormControlLabel value="DECLARE_LOST" control={<Radio color="error" />}
                label={t("inventory.investigationDecisions.DECLARE_LOST")} />
            </> : <>
              <FormControlLabel value="SCAN_ERROR" control={<Radio />}
                label={t("inventory.investigationDecisions.SCAN_ERROR")} />
              <FormControlLabel value="QUARANTINED_UNKNOWN" control={<Radio color="warning" />}
                label={t("inventory.investigationDecisions.QUARANTINED_UNKNOWN")} />
            </>}
          </RadioGroup>
          {investigationDecision === "FOUND_ELSEWHERE" && <TextField required fullWidth
            label={t("inventory.dialogs.decisionLocation")} value={investigationLocation}
            onChange={(event) => setInvestigationLocation(event.target.value)} />}
          {investigationDecision === "DECLARE_LOST" && <Alert severity="error">
            {t("inventory.dialogs.lostWarning")}
          </Alert>}
          <TextField required fullWidth multiline minRows={2} label={t("inventory.dialogs.finalReason")}
            value={investigationReason} onChange={(event) => setInvestigationReason(event.target.value)} />
          {resolveInvestigation.error && <Alert severity="error">
            {resolveInvestigation.error instanceof Error ? resolveInvestigation.error.message : t("inventory.dialogs.decisionError")}
          </Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setInvestigationToResolve(null)} disabled={resolveInvestigation.isPending}>{t("inventory.actions.cancel")}</Button>
        <Button variant="contained" color={investigationDecision === "DECLARE_LOST" ? "error" : "primary"}
          disabled={!investigationDecision || investigationReason.trim().length < 3 ||
            (investigationDecision === "FOUND_ELSEWHERE" && !investigationLocation.trim()) || resolveInvestigation.isPending}
          onClick={() => resolveInvestigation.mutate()}>{t("inventory.actions.validateDecision")}</Button>
      </DialogActions>
    </Dialog>

    <Dialog open={Boolean(campaignToCancel)}
      onClose={() => !cancelCampaign.isPending && setCampaignToCancel(null)} fullWidth maxWidth="sm">
      <DialogTitle>{t("inventory.dialogs.cancelTitle")}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="warning">
            {t("inventory.dialogs.cancelWarning", { campaign: campaignToCancel?.campaignNumber })}
          </Alert>
          <TextField autoFocus required multiline minRows={3} label={t("inventory.dialogs.cancelReason")}
            value={cancellationReason} inputProps={{ maxLength: 500 }}
            onChange={(event) => setCancellationReason(event.target.value)}
            helperText={t("inventory.dialogs.cancelHelp")} />
          {cancelCampaign.isError && <Alert severity="error">
            {cancelCampaign.error instanceof Error ? cancelCampaign.error.message : t("inventory.dialogs.cancelError")}
          </Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCampaignToCancel(null)} disabled={cancelCampaign.isPending}>{t("inventory.actions.keep")}</Button>
        <Button variant="contained" color="error" startIcon={<CancelOutlined />}
          disabled={cancellationReason.trim().length < 3 || cancelCampaign.isPending}
          onClick={() => cancelCampaign.mutate()}>{t("inventory.actions.confirmCancel")}</Button>
      </DialogActions>
    </Dialog>

    <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="lg">
      <DialogTitle>{editing ? `${editing.campaignNumber} · ${t("inventory.form.edit")}` : t("inventory.form.create")}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}><TextField fullWidth required label={t("inventory.form.name")} value={draft.displayName} onChange={(e) => setDraft({ ...draft, displayName: e.target.value })} /></Grid>
            <Grid size={{ xs: 12, md: 3 }}><TextField select fullWidth label={t("inventory.form.warehouse")} value={selectedWarehouse} onChange={(e) => setDraft({ ...draft, warehouseId: e.target.value, scopes: [] })}>{warehouses.map((warehouse) => <MenuItem key={warehouse.id} value={warehouse.id}>{warehouse.code} · {warehouse.displayName}</MenuItem>)}</TextField></Grid>
            <Grid size={{ xs: 12, md: 3 }}><TextField select fullWidth label={t("inventory.form.client")} value={draft.clientId ?? ""} onChange={(e) => setDraft({ ...draft, clientId: e.target.value || null })}><MenuItem value="">{t("inventory.form.allClients")}</MenuItem>{(clients.data ?? []).filter((client) => client.status === "ACTIVE").map((client) => <MenuItem key={client.id} value={client.id}>{client.displayName}</MenuItem>)}</TextField></Grid>
            <Grid size={{ xs: 12, md: 3 }}><TextField fullWidth type="date" label={t("inventory.form.deadline")} value={draft.countDeadline ?? ""} onChange={(e) => setDraft({ ...draft, countDeadline: e.target.value || null })} slotProps={{ inputLabel: { shrink: true } }} /></Grid>
          </Grid>

          <Box><Typography variant="h6" fontWeight={800} gutterBottom>{t("inventory.form.inventoryType")}</Typography>
            <Grid container spacing={1.5}>{(["GENERAL", "TARGETED", "ABC"] as const).map((method) =>
              <Grid key={method} size={{ xs: 12, md: 4 }}><Card variant="outlined" sx={{ height: "100%", borderColor: draft.selectionMethod === method ? "primary.main" : "divider", borderWidth: draft.selectionMethod === method ? 2 : 1 }}>
                <CardActionArea sx={{ height: "100%" }} onClick={() => setDraft({ ...draft,
                  selectionMethod: method,
                  scopeMode: method === "GENERAL" ? "WAREHOUSE" : "SELECTED_LOCATIONS",
                  scopes: [], abcClasses: method === "ABC" ? (draft.abcClasses.length ? draft.abcClasses : ["A"]) : []
                })}><CardContent><Stack direction="row" spacing={1}><Radio checked={draft.selectionMethod === method} /><Box>
                  <Typography fontWeight={800}>{t(`inventory.selection.${method}.title`)}</Typography>
                  <Typography variant="body2" color="text.secondary">{t(`inventory.selection.${method}.description`)}</Typography>
                </Box></Stack></CardContent></CardActionArea>
              </Card></Grid>)}</Grid>
            {draft.selectionMethod === "ABC" && <Stack spacing={1.5} sx={{ mt: 2 }}>
              {!draft.clientId && <Alert severity="info">{t("inventory.selection.clientRequired")}</Alert>}
              <Box><Typography fontWeight={700} gutterBottom>{t("inventory.selection.classes")}</Typography>
                <Stack direction="row" spacing={1}>{(["A", "B", "C"] as const).map((abcClass) =>
                  <FormControlLabel key={abcClass} control={<Checkbox checked={draft.abcClasses.includes(abcClass)}
                    onChange={() => setDraft({ ...draft, abcClasses: draft.abcClasses.includes(abcClass)
                      ? draft.abcClasses.filter((value) => value !== abcClass) : [...draft.abcClasses, abcClass].sort() as Array<"A" | "B" | "C"> })} />}
                    label={`${t("inventory.selection.class")} ${abcClass}`} />)}</Stack>
              </Box>
              {abcPreview.data && <Alert severity={abcPreview.data.materialCount ? "success" : "warning"}>
                <Typography variant="body2">
                  {t("inventory.selection.abcPreview", { materials: abcPreview.data.materialCount, locations: abcPreview.data.locationCount })}
                  {abcPreview.data.unclassifiedMaterialCount > 0 ? ` ${t("inventory.selection.unclassified", { count: abcPreview.data.unclassifiedMaterialCount })}` : ""}
                </Typography>
                {abcPreview.data.materialCount === 0 && abcPreview.data.notDueMaterials.length > 0 && <Stack spacing={0.5} sx={{ mt: 1 }}>
                  <Typography variant="body2" fontWeight={700}>{t("inventory.selection.noneDue")}</Typography>
                  <Accordion disableGutters elevation={0} sx={{ bgcolor: "transparent", "&:before": { display: "none" } }}>
                    <AccordionSummary sx={{ minHeight: 36, px: 0, justifyContent: "flex-start", "& .MuiAccordionSummary-content": { my: 0.5, flexGrow: 0, ml: 0, mr: 0 }, "&.Mui-expanded .not-due-chevron": { transform: "rotate(180deg)" } }}>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant="body2" fontWeight={700}>
                          {t("inventory.selection.notDueDetails", { count: abcPreview.data.notDueMaterials.length })}
                        </Typography>
                        <ExpandMoreOutlined className="not-due-chevron" fontSize="small" sx={{ transition: "transform 150ms" }} />
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 0, pt: 0 }}>
                      <Stack spacing={0.5}>{abcPreview.data.notDueMaterials.map((material) => {
                        const frequency = material.abcClass === "A" ? abcPreview.data.classAFrequencyDays
                          : material.abcClass === "B" ? abcPreview.data.classBFrequencyDays : abcPreview.data.classCFrequencyDays;
                        const daysUntilDue = material.nextCountDueOn
                          ? Math.max(0, Math.ceil((new Date(`${material.nextCountDueOn}T00:00:00Z`).getTime() - Date.now()) / 86_400_000))
                          : 0;
                        return <Typography key={material.itemId} variant="body2">
                          {t("inventory.selection.notDueDetail", {
                            material: material.itemCode,
                            abcClass: material.abcClass,
                            lastCounted: material.lastCountedOn ? new Date(`${material.lastCountedOn}T00:00:00Z`).toLocaleDateString(locale) : "—",
                            frequency,
                            nextDue: material.nextCountDueOn ? new Date(`${material.nextCountDueOn}T00:00:00Z`).toLocaleDateString(locale) : "—",
                            days: daysUntilDue
                          })}
                        </Typography>;
                      })}</Stack>
                    </AccordionDetails>
                  </Accordion>
                </Stack>}
              </Alert>}
            </Stack>}
          </Box>

          <Box><Typography variant="h6" fontWeight={800} gutterBottom>{t("inventory.form.what")}</Typography>
            <Grid container spacing={1.5}>{countModes.map((mode) => <Grid key={mode.value} size={{ xs: 12, md: 4 }}><Card variant="outlined" sx={{ height: "100%", borderColor: draft.countMode === mode.value ? "primary.main" : "divider", borderWidth: draft.countMode === mode.value ? 2 : 1 }}><CardActionArea sx={{ height: "100%" }} onClick={() => setDraft({ ...draft, countMode: mode.value })}><CardContent><Stack direction="row" spacing={1}><Radio checked={draft.countMode === mode.value} /><Box><Typography fontWeight={800}>{mode.title}{mode.value === "HU_AND_CONTENT" && <Chip size="small" label={t("inventory.form.recommended")} color="info" sx={{ ml: 1 }} />}</Typography><Typography variant="body2" color="text.secondary">{mode.description}</Typography></Box></Stack></CardContent></CardActionArea></Card></Grid>)}</Grid>
          </Box>

          {draft.selectionMethod === "TARGETED" && <Box><Typography variant="h6" fontWeight={800}>{t("inventory.form.perimeter")}</Typography>
            <RadioGroup row value={draft.scopeMode} onChange={(e) => setDraft({ ...draft, scopeMode: e.target.value as InventoryCountCampaignMutation["scopeMode"], scopes: [] })}>
              <FormControlLabel value="WAREHOUSE" control={<Radio />} label={t("inventory.form.wholeWarehouse")} />
              <FormControlLabel value="SELECTED_LOCATIONS" control={<Radio />} label={t("inventory.form.selectedLocations")} />
            </RadioGroup>
            {draft.scopeMode === "SELECTED_LOCATIONS" && <Stack spacing={1} sx={{ mt: 1 }}>
              {areas.map((area) => <Accordion key={area.id} variant="outlined"><AccordionSummary expandIcon={<ExpandMoreOutlined />}><Checkbox checked={selected("STORAGE_AREA", area.id)} onClick={(e) => e.stopPropagation()} onChange={() => toggleScope({ locationType: "STORAGE_AREA", locationId: area.id })} /><Box><Typography fontWeight={700}>{area.code} · {area.displayName}</Typography><Typography variant="caption" color="text.secondary">{t("inventory.card.locations", { count: (binsByArea.get(area.id) ?? []).length })}</Typography></Box></AccordionSummary><AccordionDetails><Stack direction="row" flexWrap="wrap" gap={1}>{(binsByArea.get(area.id) ?? []).map((bin) => <FormControlLabel key={bin.id} control={<Checkbox checked={selected("STORAGE_BIN", bin.id)} disabled={selected("STORAGE_AREA", area.id)} onChange={() => toggleScope({ locationType: "STORAGE_BIN", locationId: bin.id })} />} label={bin.code} />)}</Stack></AccordionDetails></Accordion>)}
              {points.length > 0 && <Accordion variant="outlined"><AccordionSummary expandIcon={<ExpandMoreOutlined />}><Typography fontWeight={700}>{t("inventory.form.processAreas")}</Typography></AccordionSummary><AccordionDetails><Stack direction="row" flexWrap="wrap" gap={1}>{points.map((point) => <FormControlLabel key={point.id} control={<Checkbox checked={selected("PROCESS_POINT", point.id)} onChange={() => toggleScope({ locationType: "PROCESS_POINT", locationId: point.id })} />} label={`${point.code} · ${point.displayName}`} />)}</Stack></AccordionDetails></Accordion>}
            </Stack>}
          </Box>}

          <Card variant="outlined"><CardContent><Stack direction="row" spacing={2} alignItems="center"><LockOutlined color="primary" /><Box sx={{ flex: 1 }}><Typography fontWeight={800}>{t("inventory.form.blindTitle")}</Typography><Typography variant="body2" color="text.secondary">{t("inventory.form.blindHelp")}</Typography></Box><Switch checked={draft.blindCount} onChange={(e) => setDraft({ ...draft, blindCount: e.target.checked })} /></Stack></CardContent></Card>

          <Card variant="outlined" sx={{ bgcolor: "action.hover" }}><CardContent><Typography variant="h6" fontWeight={800} gutterBottom>{t("inventory.form.preview")}</Typography>{preview.isFetching ? <Typography>{t("inventory.form.calculating")}</Typography> : preview.data ? <Grid container spacing={2}><Grid size={{ xs: 6, md: 2.4 }}><Typography variant="h5" fontWeight={800}>{preview.data.locationCount}</Typography><Typography color="text.secondary">{t("inventory.form.locations")}</Typography></Grid><Grid size={{ xs: 6, md: 2.4 }}><Typography variant="h5" fontWeight={800}>{preview.data.handlingUnitCount}</Typography><Typography color="text.secondary">{t("inventory.form.expectedHu")}</Typography></Grid><Grid size={{ xs: 6, md: 2.4 }}><Typography variant="h5" fontWeight={800}>{preview.data.contentLineCount}</Typography><Typography color="text.secondary">{t("inventory.form.contentLines")}</Typography></Grid><Grid size={{ xs: 6, md: 2.4 }}><Typography variant="h5" fontWeight={800}>{preview.data.emptyLocationCount}</Typography><Typography color="text.secondary">{t("inventory.form.emptyLocations")}</Typography></Grid><Grid size={{ xs: 6, md: 2.4 }}><Typography variant="h5" fontWeight={800} color={preview.data.unlocatedHandlingUnitCount ? "error.main" : "text.primary"}>{preview.data.unlocatedHandlingUnitCount}</Typography><Typography color="text.secondary">{t("inventory.form.unlocatedHu")}</Typography></Grid></Grid> : <Typography color="text.secondary">{t("inventory.form.define")}</Typography>}{preview.data?.unlocatedHandlingUnitCount ? <Alert severity="warning" sx={{ mt: 2 }}>{t("inventory.form.unlocatedWarning")}</Alert> : null}</CardContent></Card>
          {(save.error || preview.error || abcPreview.error) && <Alert severity="error">{String((save.error ?? preview.error ?? abcPreview.error) instanceof Error ? (save.error ?? preview.error ?? abcPreview.error)?.message : t("inventory.errors.load"))}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions><Button onClick={() => setDialogOpen(false)}>{t("inventory.actions.close")}</Button><Button variant="contained" disabled={!draft.displayName.trim() || !selectedWarehouse || !scopeReady || save.isPending} onClick={() => save.mutate()}>{t("inventory.actions.saveDraft")}</Button></DialogActions>
    </Dialog>

    <Dialog open={Boolean(campaignToPrepare)} onClose={() => !prepare.isPending && setCampaignToPrepare(null)} fullWidth maxWidth="sm">
      <DialogTitle>{t("inventory.prepare.title")}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info">{t("inventory.prepare.warning")}</Alert>
          <Box>
            <Typography fontWeight={800}>{campaignToPrepare?.campaignNumber} · {campaignToPrepare?.displayName}</Typography>
            <Typography color="text.secondary">
              {campaignToPrepare?.warehouseCode} · {campaignToPrepare?.clientName ? t("inventory.card.client", { name: campaignToPrepare.clientName }) : t("inventory.card.allClients")}
            </Typography>
            <Typography color="text.secondary">
              {campaignToPrepare?.scopeMode === "WAREHOUSE" ? t("inventory.prepare.wholeWarehouse") : t("inventory.prepare.selections", { count: campaignToPrepare?.scopes.length ?? 0 })} · {countModes.find((mode) => mode.value === campaignToPrepare?.countMode)?.title}
            </Typography>
          </Box>
          {prepare.isError && <Alert severity="error">{prepare.error instanceof Error ? prepare.error.message : t("inventory.prepare.error")}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={prepare.isPending} onClick={() => setCampaignToPrepare(null)}>{t("inventory.actions.cancel")}</Button>
        <Button variant="contained" disabled={!campaignToPrepare || prepare.isPending}
          onClick={() => campaignToPrepare && prepare.mutate(campaignToPrepare)}>{t("inventory.actions.freeze")}</Button>
      </DialogActions>
    </Dialog>

    <Dialog open={Boolean(campaignToCount)} onClose={() => { setCampaignToCount(null); setResolutionMode(false); }} fullWidth
      maxWidth={resolutionMode ? "md" : "sm"}
      sx={{ "& .MuiDialog-paper": {
        m: { xs: 0, sm: 2 }, width: { xs: "100%", sm: "calc(100% - 32px)" },
        height: { xs: "100%", sm: "auto" },
        maxHeight: { xs: "100%", sm: "calc(100% - 32px)" },
        borderRadius: { xs: 0, sm: 1.5 }
      } }}>
      <DialogTitle>{campaignToCount?.campaignNumber} · {resolutionMode ? t("inventory.counting.discrepancies") : t("inventory.counting.field")}</DialogTitle>
      <DialogContent dividers>
        {countTasks.isLoading && <Typography>{t("inventory.counting.loading")}</Typography>}
        {!countTasks.isLoading && !currentTask && discrepancyTasks.length === 0 && <Alert severity="success">{t("inventory.counting.allCounted")}</Alert>}
        {!countTasks.isLoading && !currentTask && discrepancyTasks.length > 0 && !resolutionMode && <Alert severity="warning">
          {t("inventory.counting.fieldDone")}
        </Alert>}
        {!countTasks.isLoading && !currentTask && discrepancyTasks.length > 0 && resolutionMode && <Stack spacing={2}>
          <Alert severity="warning">
            {t("inventory.counting.discrepancySummary", { count: discrepancyTasks.length })}
          </Alert>
          <TextField fullWidth required label={t("inventory.counting.decisionReason")} value={resolutionReason}
            helperText={t("inventory.counting.decisionHelp")}
            onChange={(event) => setResolutionReason(event.target.value)} />
          <TextField select fullWidth required label={t("inventory.counting.structuredCause")}
            value={resolutionCause}
            onChange={(event) => setResolutionCause(event.target.value as InventoryCountDiscrepancyCause)}>
            {discrepancyCauses.map((cause) => <MenuItem key={cause.value} value={cause.value}>
              {cause.label}
            </MenuItem>)}
          </TextField>
          {(resolutionReason.trim().length < 3 || !resolutionCause) && <Alert severity="info">
            {t("inventory.counting.decisionRequired")}
          </Alert>}
          {discrepancyTasks.map((task) => <Card key={task.id} variant="outlined">
            <CardContent>
              <Stack spacing={2}>
                <Box>
                  <Typography fontWeight={800}>{task.locationCode}</Typography>
                  {task.locationName && <Typography variant="body2" color="text.secondary">{task.locationName}</Typography>}
                </Box>
                <Grid container spacing={2.5}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="caption" color="text.secondary">{t("inventory.counting.matching")}</Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 0.5 }}>
                      {task.matchingHuNumbers.map((hu) => <Chip key={hu} size="small" color="success" label={hu} />)}
                      {task.matchingHuNumbers.length === 0 && <Typography variant="body2">{t("inventory.counting.none")}</Typography>}
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="caption" color="text.secondary">{t("inventory.counting.missing")}</Typography>
                    <Stack spacing={1} sx={{ mt: 0.5 }}>
                      {task.missingHus.map((hu) => <Box key={hu.huNumber} sx={{ p: 1.25, border: 1, borderColor: "divider", borderRadius: 1.5 }}>
                        <Stack spacing={1} alignItems="flex-start">
                          <Chip size="small" color="error" variant="outlined" label={hu.huNumber} />
                          <Button size="small" variant="outlined" fullWidth
                            disabled={!resolutionCause || resolutionReason.trim().length < 3 || resolveDiscrepancy.isPending}
                          onClick={() => resolveDiscrepancy.mutate({ task, hu: hu.huNumber, decision: "SEARCH_MISSING" })}>
                            {t("inventory.extra.openSearch")}
                          </Button>
                        </Stack>
                      </Box>)}
                      {task.missingHuNumbers.length === 0 && <Typography variant="body2">{t("inventory.counting.none")}</Typography>}
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="caption" color="text.secondary">{t("inventory.counting.unexpected")}</Typography>
                    <Stack spacing={1} sx={{ mt: 0.5 }}>
                      {task.unexpectedHus.map((hu) => <Box key={hu.huNumber} sx={{ p: 1.25, border: 1, borderColor: "divider", borderRadius: 1.5 }}>
                        <Stack spacing={1}>
                          <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.75}>
                          <Chip size="small" color="warning" label={hu.huNumber} />
                          <Chip size="small" variant="outlined"
                            label={hu.known ? t("inventory.extra.knownAt", { location: hu.currentLocationCode ?? t("inventory.extra.withoutLocation") }) : t("inventory.extra.unknownHu")} />
                          </Stack>
                          <Stack spacing={0.75}>
                          <Button size="small" color="error" variant="outlined" fullWidth
                            disabled={!resolutionCause || resolutionReason.trim().length < 3 || removeObservation.isPending}
                            onClick={() => removeObservation.mutate({ task, hu: hu.huNumber })}>{t("inventory.counting.scanError")}</Button>
                          {hu.known ? <Button size="small" variant="contained" fullWidth
                            disabled={!resolutionCause || resolutionReason.trim().length < 3 || resolveDiscrepancy.isPending}
                            onClick={() => resolveDiscrepancy.mutate({ task, hu: hu.huNumber, decision: "REGULARIZE_LOCATION" })}>
                            {t("inventory.extra.regularizeHere")}
                          </Button> : <Button size="small" variant="contained" fullWidth
                            disabled={!resolutionCause || resolutionReason.trim().length < 3 || resolveDiscrepancy.isPending}
                            onClick={() => resolveDiscrepancy.mutate({ task, hu: hu.huNumber, decision: "INVESTIGATE_UNKNOWN" })}>
                            {t("inventory.extra.investigate")}
                          </Button>}
                          </Stack>
                        </Stack>
                      </Box>)}
                      {task.unexpectedHuNumbers.length === 0 && <Typography variant="body2">{t("inventory.counting.none")}</Typography>}
                    </Stack>
                  </Grid>
                </Grid>
                {task.contentLines.some((line) => line.discrepancy) && <Box
                  sx={{ pt: 1.5, borderTop: 1, borderColor: "divider" }}>
                  <Typography fontWeight={800} gutterBottom>{t("inventory.counting.contentDiscrepancies")}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                    {t("inventory.extra.supervisorFindings")}
                  </Typography>
                  <Stack spacing={1}>
                    {task.contentLines.filter((line) => line.discrepancy).map((line) => <Box
                      key={`difference-${line.snapshotId ?? `${line.huNumber}-${line.itemCode}-${line.lotNumber}-${line.serialNumber}`}`}
                      sx={{ p: 1.25, border: 1, borderColor: "warning.light", borderRadius: 1.5,
                        bgcolor: "warning.50" }}>
                      <Stack spacing={1.5}>
                      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}
                        alignItems={{ md: "center" }} justifyContent="space-between">
                        <Box>
                          <Stack direction="row" flexWrap="wrap" gap={0.75} alignItems="center">
                            <Chip size="small" variant="outlined" label={line.huNumber} />
                            <Typography fontWeight={750}>{line.itemCode} · {line.itemName}</Typography>
                            {line.unexpected && <Chip size="small" color="warning" label={t("inventory.counting.unexpectedContent")} />}
                            {line.remediationStatus === "OPEN" && <Chip size="small" color="info"
                              label={t("inventory.status.investigationOpen")} />}
                          </Stack>
                          {(line.lotNumber || line.serialNumber) && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {line.lotNumber ? t("inventory.extra.lotValue", { value: line.lotNumber }) : ""}
                            {line.lotNumber && line.serialNumber ? " · " : ""}
                            {line.serialNumber ? t("inventory.extra.serialValue", { value: line.serialNumber }) : ""}
                          </Typography>}
                        </Box>
                        <Stack direction="row" spacing={2.5} sx={{ minWidth: { md: 300 } }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">{t("inventory.counting.expected")}</Typography>
                            <Typography fontWeight={750}>
                              {line.expectedQuantity == null ? t("inventory.extra.absentValue") : `${line.expectedQuantity} ${line.uom}`}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">{t("inventory.counting.observed")}</Typography>
                            <Typography fontWeight={750} color="warning.dark">
                              {line.observedQuantity == null ? t("inventory.counting.notObserved") : `${line.observedQuantity} ${line.uom}`}
                            </Typography>
                          </Box>
                        </Stack>
                      </Stack>
                      {line.serialNumber && line.observedQuantity != null
                        && Number(line.observedQuantity) !== 1 && <Alert severity="info">
                          {t("inventory.extra.serializedHelp")}
                        </Alert>}
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}
                        justifyContent="flex-end" alignItems={{ xs: "stretch", sm: "center" }}
                        flexWrap="wrap" useFlexGap>
                        {canSupervise && line.snapshotId && line.observedQuantity != null && <Button size="small" variant="outlined"
                          disabled={!(["COUNT_ERROR", "DATA_ENTRY_ERROR"] as string[]).includes(resolutionCause)
                            || resolutionReason.trim().length < 3 || remediateContent.isPending}
                          onClick={() => remediateContent.mutate({
                            task, snapshotId: line.snapshotId!, decision: "OBSERVATION_CORRECTED"
                          })}>
                          {t("inventory.counting.settleExpected", { quantity: line.expectedQuantity, uom: line.uom })}
                        </Button>}
                        {canAdjust && line.snapshotId && !line.serialNumber && line.observedQuantity != null
                          && Number(line.observedQuantity) > 0 && <Button
                          size="small" variant="contained" color="warning"
                          disabled={!resolutionCause || (["COUNT_ERROR", "DATA_ENTRY_ERROR"] as string[]).includes(resolutionCause)
                            || resolutionReason.trim().length < 3 || remediateContent.isPending}
                          onClick={() => setContentAdjustmentToConfirm({ task, line })}>
                          {t("inventory.counting.validateAdjustment", { expected: line.expectedQuantity, observed: line.observedQuantity, uom: line.uom })}
                        </Button>}
                        {canSupervise && line.snapshotId && <Button size="small" variant="outlined" color="warning"
                          disabled={!resolutionCause || resolutionReason.trim().length < 3 || remediateContent.isPending}
                          onClick={() => remediateContent.mutate({
                            task, snapshotId: line.snapshotId!, decision: "INVESTIGATION_OPENED"
                          })}>
                          Mettre en investigation
                        </Button>}
                      </Stack>
                      </Stack>
                    </Box>)}
                  </Stack>
                </Box>}
                <Grid container spacing={2.5} sx={{ pt: 1.5, borderTop: 1, borderColor: "divider" }}>
                  <Grid size={{ xs: 12, md: 8 }} />
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Button size="small" variant="outlined" color="warning" fullWidth
                      sx={{ display: canSupervise ? "inline-flex" : "none" }}
                      disabled={recount.isPending}
                      onClick={() => { setTaskToRecount(task); setRecountComment(""); }}>
                      {t("inventory.counting.requestRecount")}
                    </Button>
                  </Grid>
                </Grid>
              </Stack>
            </CardContent>
          </Card>)}
          {(recount.error || removeObservation.error || resolveDiscrepancy.error || remediateContent.error) && <Alert severity="error">
            {(recount.error ?? removeObservation.error ?? resolveDiscrepancy.error ?? remediateContent.error) instanceof Error
              ? (recount.error ?? removeObservation.error ?? resolveDiscrepancy.error ?? remediateContent.error)?.message
              : t("inventory.counting.saveError")}
          </Alert>}
        </Stack>}
        {currentTask && <Stack spacing={2}>
          <Box>
            <Typography variant="overline">{t("inventory.step", { current: currentTask.sequenceNumber, total: countTasks.data?.length ?? 0 })}</Typography>
            <Typography variant="h5" fontWeight={800}>{currentTask.locationCode}</Typography>
            {currentTask.locationName && <Typography color="text.secondary">{currentTask.locationName}</Typography>}
          </Box>
          {!currentTask.locationConfirmed ? <>
            <Alert severity="info">{t("inventory.counting.goLocation")}</Alert>
            <TextField autoFocus fullWidth label={t("inventory.counting.scanLocation")} value={locationScan}
              onChange={(event) => setLocationScan(event.target.value)}
              onKeyDown={(event) => { if (event.key === "Enter" && locationScan.trim()) startCount.mutate({ task: currentTask, code: locationScan }); }} />
            <Button variant="contained" startIcon={<QrCodeScannerOutlined />} disabled={!locationScan.trim() || startCount.isPending}
              onClick={() => startCount.mutate({ task: currentTask, code: locationScan })}>{t("inventory.counting.validateLocation")}</Button>
          </> : <>
            {isTargetedRecount ? <Alert severity="warning">
              <Typography fontWeight={800}>{t("inventory.counting.targeted")}</Typography>
              <Typography variant="body2">
                {t("inventory.counting.targetedInstruction", { count: currentRecountHuNumbers.length })}
                {currentConformingHuNumbers.length > 0
                  ? ` ${t("inventory.counting.conformingInstruction", { count: currentConformingHuNumbers.length })}`
                  : ""}
              </Typography>
            </Alert> : <Alert severity="info">{currentTask.blindCount
              ? t("inventory.counting.blindInstruction")
              : t("inventory.counting.visibleInstruction")}</Alert>}
            {isTargetedRecount && <Box sx={{ p: 1.5, border: 1, borderColor: "warning.light", borderRadius: 1.5,
              bgcolor: "warning.50" }}>
              <Typography fontWeight={800} color="warning.dark">
                {t("inventory.counting.toRecount", { count: currentRecountHuNumbers.length })}
              </Typography>
              <Stack spacing={1} sx={{ mt: 1 }}>
                {currentRecountHuNumbers.map((hu) => <Box key={hu}
                  sx={{ p: 1.25, bgcolor: "background.paper", borderRadius: 1, border: 1, borderColor: "warning.light" }}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                    <Chip color={isRecountHuCompleted(hu) ? "success" : "warning"}
                      label={hu} />
                    <Box sx={{ flex: 1 }}>
                      {currentRecountTargets.filter((target) => target.huNumber === hu).map((target, index) =>
                        <Typography key={`${target.targetType}-${target.itemCode ?? ""}-${index}`}
                          variant="body2" color="text.secondary">
                          {target.targetType === "CONTENT"
                            ? t("inventory.counting.targetContent", { item: target.itemCode, trace: target.serialNumber ? ` · S/N ${target.serialNumber}` : target.lotNumber ? ` · lot ${target.lotNumber}` : "", expected: target.expectedQuantity ?? "—", observed: target.observedQuantity ?? t("inventory.counting.notObserved").toLocaleLowerCase(locale), uom: target.uom ?? "" })
                            : t("inventory.counting.targetHu")}
                        </Typography>)}
                    </Box>
                  </Stack>
                </Box>)}
              </Stack>
            </Box>}
            {isTargetedRecount && currentConformingHuNumbers.length > 0 && <Box
              sx={{ p: 1.25, border: 1, borderColor: "success.light", borderRadius: 1.5, bgcolor: "success.50" }}>
              <Typography variant="body2" fontWeight={750} color="success.dark">
                {t("inventory.counting.alreadyConforming", { count: currentConformingHuNumbers.length })}
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 0.75 }}>
                {currentConformingHuNumbers.map((hu) => <Chip key={hu} size="small" color="success"
                  variant="outlined" label={hu} />)}
              </Stack>
            </Box>}
            {!currentTask.blindCount && !isTargetedRecount && <Box>
              <Typography variant="caption" color="text.secondary">{t("inventory.counting.expectedHu", { count: currentTask.expectedHuNumbers.length })}</Typography>
              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 0.75 }}>
                {currentTask.expectedHuNumbers.map((hu) => <Chip key={hu} label={hu}
                  color={currentTask.observedHuNumbers.includes(hu) ? "success" : "default"}
                  variant={currentTask.observedHuNumbers.includes(hu) ? "filled" : "outlined"} />)}
                {currentTask.expectedHuNumbers.length === 0 && <Typography color="text.secondary">{t("inventory.counting.noExpected")}</Typography>}
              </Stack>
            </Box>}
            {!activeHuNumber && <>
              <TextField autoFocus fullWidth label={t("inventory.counting.scanHu")} value={huScan}
                onChange={(event) => { setHuScan(event.target.value); setConformingHuScanWarning(null); }}
                onKeyDown={(event) => { if (event.key === "Enter") submitHuScan(currentTask, huScan); }} />
              <Button variant="outlined" startIcon={<QrCodeScannerOutlined />} disabled={!huScan.trim() || observeHu.isPending}
                onClick={() => submitHuScan(currentTask, huScan)}>{t("inventory.counting.addHu")}</Button>
              {conformingHuScanWarning && <Alert severity="warning"
                action={<Stack direction={{ xs: "column", sm: "row" }} spacing={0.5}>
                  <Button size="small" color="inherit"
                    onClick={() => { setConformingHuScanWarning(null); setHuScan(""); }}>{t("inventory.counting.dontRecount")}</Button>
                  <Button size="small" color="warning" variant="contained"
                    onClick={() => submitHuScan(currentTask, conformingHuScanWarning, true)}>{t("inventory.counting.recountAnyway")}</Button>
                </Stack>}>
                <Typography fontWeight={750}>{t("inventory.counting.wasConforming", { hu: conformingHuScanWarning })}</Typography>
                <Typography variant="body2">{t("inventory.counting.outsideTarget")}</Typography>
              </Alert>}
            </>}
            {activeHuNumber && currentTask.countMode !== "HU_ONLY" && <Card variant="outlined">
              <CardContent>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="overline">{t("inventory.counting.contentControl")}</Typography>
                    <Typography variant="h6" fontWeight={800}>{activeHuNumber}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("inventory.extra.contentInstruction")}
                    </Typography>
                  </Box>
                  {!currentTask.blindCount && currentTask.contentLines
                    .filter((line) => line.huNumber === activeHuNumber)
                    .map((line) => <Box key={line.snapshotId ?? `${line.itemCode}-${line.lotNumber}-${line.serialNumber}`}
                      sx={{ p: 1.5, border: 1, borderColor: "divider", borderRadius: 1.5 }}>
                      <Stack spacing={1.5}>
                        <Box sx={{ width: "100%" }}>
                          <Typography fontWeight={750}>{line.itemCode} · {line.itemName}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {line.lotNumber ? `Lot ${line.lotNumber} · ` : ""}
                            {line.serialNumber ? <>{t("inventory.counting.expectedSerial")} <strong>{line.serialNumber}</strong></> : <>{t("inventory.counting.expectedQuantity", { quantity: line.expectedQuantity, uom: line.uom })}</>}
                          </Typography>
                        </Box>
                        {line.serialNumber ? <Stack spacing={1.25}>
                          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                            <Chip size="small"
                              color={line.observedQuantity === 1 ? "success" : line.observedQuantity === 0 ? "warning" : "default"}
                              label={line.observedQuantity === 1 ? t("inventory.counting.present") : line.observedQuantity === 0 ? t("inventory.counting.absent") : t("inventory.counting.toScan")} />
                          </Stack>
                          <TextField fullWidth size="small" label={t("inventory.counting.scanSerial")}
                              value={contentSerialScans[line.snapshotId ?? line.serialNumber] ?? ""}
                              onChange={(event) => setContentSerialScans({
                                ...contentSerialScans,
                                [line.snapshotId ?? line.serialNumber!]: event.target.value
                              })}
                              onKeyDown={(event) => {
                                const scanned = (contentSerialScans[line.snapshotId ?? line.serialNumber!] ?? "").trim().toUpperCase();
                                if (event.key === "Enter" && scanned) {
                                  observeContent.mutate({ task: currentTask, request: {
                                    huNumber: activeHuNumber,
                                    snapshotId: scanned === line.serialNumber!.toUpperCase() ? line.snapshotId : null,
                                    itemCode: line.itemCode, lotNumber: line.lotNumber,
                                    serialNumber: scanned, quantity: 1, uom: line.uom
                                  } });
                                }
                              }} />
                          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="flex-end">
                            <Button variant="contained" startIcon={<QrCodeScannerOutlined />}
                              disabled={!(contentSerialScans[line.snapshotId ?? line.serialNumber] ?? "").trim()
                                || observeContent.isPending}
                              onClick={() => {
                                const scanned = (contentSerialScans[line.snapshotId ?? line.serialNumber!] ?? "").trim().toUpperCase();
                                observeContent.mutate({ task: currentTask, request: {
                                  huNumber: activeHuNumber,
                                  snapshotId: scanned === line.serialNumber!.toUpperCase() ? line.snapshotId : null,
                                  itemCode: line.itemCode, lotNumber: line.lotNumber,
                                  serialNumber: scanned, quantity: 1, uom: line.uom
                                } });
                              }}>{t("inventory.counting.validateSerial")}</Button>
                            <Button variant={line.observedQuantity === 0 ? "contained" : "outlined"}
                              color="warning" disabled={observeContent.isPending}
                              onClick={() => observeContent.mutate({ task: currentTask, request: {
                                huNumber: activeHuNumber, snapshotId: line.snapshotId,
                                itemCode: line.itemCode, lotNumber: line.lotNumber,
                                serialNumber: line.serialNumber, quantity: 0, uom: line.uom
                              } })}>{t("inventory.counting.serialAbsent")}</Button>
                          </Stack>
                        </Stack> : <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}
                          alignItems={{ sm: "stretch" }} justifyContent="flex-end">
                          <TextField fullWidth size="small" type="number" label={t("inventory.counting.observedQty")}
                            value={contentQuantities[line.snapshotId ?? line.itemCode]
                              ?? (line.observedQuantity == null ? "" : String(line.observedQuantity))}
                            onChange={(event) => setContentQuantities({
                              ...contentQuantities,
                              [line.snapshotId ?? line.itemCode]: event.target.value
                            })} sx={{ maxWidth: { sm: 260 } }} />
                          <Button variant="outlined" sx={{ minWidth: { sm: 180 } }}
                            disabled={!String(contentQuantities[line.snapshotId ?? line.itemCode]
                              ?? line.observedQuantity ?? "").length || observeContent.isPending}
                            onClick={() => observeContent.mutate({ task: currentTask, request: {
                              huNumber: activeHuNumber, snapshotId: line.snapshotId,
                              itemCode: line.itemCode, lotNumber: line.lotNumber,
                              serialNumber: line.serialNumber,
                              quantity: Number(contentQuantities[line.snapshotId ?? line.itemCode]
                                ?? line.observedQuantity), uom: line.uom
                            } })}>{t("inventory.counting.save")}</Button>
                        </Stack>}
                      </Stack>
                    </Box>)}
                  {currentTask.blindCount && <Stack spacing={1.5}>
                    <Alert severity="info">{t("inventory.counting.blindContent")}</Alert>
                    <Grid container spacing={1.5}>
                      <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label={t("inventory.form.item")}
                        value={blindContent.itemCode}
                        onChange={(event) => setBlindContent({ ...blindContent, itemCode: event.target.value })} /></Grid>
                      <Grid size={{ xs: 12, sm: 3 }}><TextField fullWidth label={t("inventory.form.lot")}
                        value={blindContent.lotNumber}
                        onChange={(event) => setBlindContent({ ...blindContent, lotNumber: event.target.value })} /></Grid>
                      <Grid size={{ xs: 12, sm: 3 }}><TextField fullWidth label={t("inventory.counting.serial")}
                        value={blindContent.serialNumber}
                        onChange={(event) => setBlindContent({ ...blindContent, serialNumber: event.target.value })} /></Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>{blindContent.serialNumber.trim()
                        ? <Alert severity="info">{t("inventory.counting.serialPresence")}</Alert>
                        : <TextField fullWidth type="number" label={t("inventory.counting.observedQuantity")}
                          value={blindContent.quantity}
                          onChange={(event) => setBlindContent({ ...blindContent, quantity: event.target.value })} />}</Grid>
                      <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label={t("inventory.counting.unit")}
                        value={blindContent.uom}
                        onChange={(event) => setBlindContent({ ...blindContent, uom: event.target.value })} /></Grid>
                    </Grid>
                    <Button variant="outlined" disabled={!blindContent.itemCode.trim()
                      || (!blindContent.serialNumber.trim() && blindContent.quantity === "")
                      || !blindContent.uom.trim() || observeContent.isPending}
                      onClick={() => observeContent.mutate({ task: currentTask, request: {
                        huNumber: activeHuNumber, snapshotId: null,
                        itemCode: blindContent.itemCode,
                        lotNumber: blindContent.lotNumber || null,
                        serialNumber: blindContent.serialNumber || null,
                        quantity: blindContent.serialNumber.trim() ? 1 : Number(blindContent.quantity), uom: blindContent.uom
                      } })}>{t("inventory.counting.addLine")}</Button>
                  </Stack>}
                  {currentTask.contentLines.some((line) => line.huNumber === activeHuNumber
                    && line.observedQuantity != null) && <Box>
                    <Typography variant="caption" color="text.secondary">{t("inventory.counting.recordedContent")}</Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 0.75 }}>
                      {currentTask.contentLines.filter((line) => line.huNumber === activeHuNumber
                        && line.observedQuantity != null).map((line) => <Chip
                          key={`observed-${line.snapshotId ?? `${line.itemCode}-${line.lotNumber}-${line.serialNumber}`}`}
                          color="info" variant="outlined"
                          label={`${line.itemCode} · ${line.observedQuantity} ${line.uom}`} />)}
                    </Stack>
                  </Box>}
                  <Button variant="contained" disabled={confirmContent.isPending
                    || !currentTask.contentLines.some((line) => line.huNumber === activeHuNumber
                      && line.observedQuantity != null)}
                    onClick={() => confirmContent.mutate({ task: currentTask, hu: activeHuNumber })}>
                    {t("inventory.extra.validateHuContent")}
                  </Button>
                </Stack>
              </CardContent>
            </Card>}
            <Box>
              <Typography variant="caption" color="text.secondary">{isTargetedRecount
                ? t("inventory.extra.recountProgress", { completed: currentRecountHuNumbers.filter(isRecountHuCompleted).length, total: currentRecountHuNumbers.length })
                : t("inventory.counting.observedHu", { count: currentTask.observedHuNumbers.length })}</Typography>
              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 0.75 }}>
                {(isTargetedRecount
                  ? currentRecountHuNumbers.filter(isRecountHuCompleted)
                  : currentTask.observedHuNumbers).map((hu) => <Chip key={hu} label={hu}
                  color={currentTask.countMode === "HU_ONLY" || currentTask.contentConfirmedHuNumbers.includes(hu)
                    ? "success" : "warning"}
                  variant="outlined" />)}
                {(isTargetedRecount
                  ? currentRecountHuNumbers.filter(isRecountHuCompleted).length === 0
                  : currentTask.observedHuNumbers.length === 0) &&
                    <Typography color="text.secondary">{t("inventory.counting.noRecountedHu")}</Typography>}
              </Stack>
            </Box>
            <Button variant="contained" disabled={submitCount.isPending || Boolean(activeHuNumber)
              || (currentTask.countMode !== "HU_ONLY"
                && !currentTask.observedHuNumbers.every((hu) => currentTask.contentConfirmedHuNumbers.includes(hu)))}
              onClick={() => submitCount.mutate({ task: currentTask, empty: currentTask.observedHuNumbers.length === 0 })}>
              {currentTask.observedHuNumbers.length === 0 ? t("inventory.extra.confirmEmptyLocation") : t("inventory.extra.finishLocation")}
            </Button>
          </>}
          {(startCount.error || observeHu.error || observeContent.error || confirmContent.error || submitCount.error) && <Alert severity="error">
            {(startCount.error ?? observeHu.error ?? observeContent.error ?? confirmContent.error ?? submitCount.error) instanceof Error
              ? (startCount.error ?? observeHu.error ?? observeContent.error ?? confirmContent.error ?? submitCount.error)?.message
              : t("inventory.counting.saveError")}
          </Alert>}
        </Stack>}
      </DialogContent>
      <DialogActions><Button onClick={() => { setCampaignToCount(null); setResolutionMode(false); }}>{t("inventory.actions.close")}</Button></DialogActions>
    </Dialog>

    <Dialog open={Boolean(campaignToApprove)}
      onClose={() => !approveCampaign.isPending && setCampaignToApprove(null)} fullWidth maxWidth="lg">
      <DialogTitle>
        {t("inventory.dialogs.approvalTitle", { campaign: campaignToApprove?.campaignNumber })}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="warning">
            {t("inventory.dialogs.approvalWarning")}
          </Alert>
          {finalReview.isLoading && <Typography>{t("inventory.dialogs.summaryLoading")}</Typography>}
          {finalReview.error && <Alert severity="error">
            {finalReview.error instanceof Error ? finalReview.error.message : t("inventory.dialogs.summaryError")}
          </Alert>}
          {finalReview.data && <>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label={t("inventory.dialogs.reviewLocations", { count: finalReview.data.locationCount })} variant="outlined" />
              <Chip label={`${finalReview.data.handlingUnitCount} HU`} variant="outlined" />
              <Chip label={t("inventory.dialogs.reviewContent", { count: finalReview.data.contentLineCount })} variant="outlined" />
              <Chip label={t("inventory.dialogs.reviewAdjustments", { count: finalReview.data.adjustmentCount })}
                color={finalReview.data.adjustmentCount > 0 ? "warning" : "success"} />
            </Stack>
            <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1.5, overflow: "hidden" }}>
              <Box sx={{ display: { xs: "none", md: "grid" }, gridTemplateColumns: "1.2fr 1.4fr .8fr .8fr .8fr 1.8fr",
                gap: 1.5, px: 2, py: 1.25, bgcolor: "grey.100" }}>
                {[t("inventory.dialogs.reviewHeaders.0"), t("inventory.dialogs.reviewHeaders.1"), t("inventory.dialogs.reviewHeaders.2"), t("inventory.dialogs.reviewHeaders.3"), t("inventory.dialogs.reviewHeaders.4"), t("inventory.dialogs.reviewHeaders.5")].map((label) =>
                  <Typography key={label} variant="caption" fontWeight={800}>{label}</Typography>)}
              </Box>
              <Stack divider={<Divider flexItem />}>
                {finalReview.data.lines.map((line, index) => <Box key={`${line.locationCode}-${line.huNumber}-${line.itemCode}-${index}`}
                  sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.2fr 1.4fr .8fr .8fr .8fr 1.8fr" },
                    gap: 1.5, px: 2, py: 1.5, bgcolor: line.decision === "STOCK_ADJUSTED" ? "warning.50" : "background.paper" }}>
                  <Box><Typography fontWeight={750}>{line.locationCode}</Typography><Typography variant="body2">{line.huNumber}</Typography></Box>
                  <Box><Typography fontWeight={750}>{line.itemCode}</Typography><Typography variant="body2" color="text.secondary">
                    {[line.lotNumber && `Lot ${line.lotNumber}`, line.serialNumber && `S/N ${line.serialNumber}`].filter(Boolean).join(" · ") || t("inventory.dialogs.noTrace")}
                  </Typography></Box>
                  <Typography>{line.theoreticalQuantity} {line.uom}</Typography>
                  <Typography color={line.observedQuantity !== line.theoreticalQuantity ? "warning.dark" : "text.primary"}
                    fontWeight={line.observedQuantity !== line.theoreticalQuantity ? 750 : 400}>{line.observedQuantity} {line.uom}</Typography>
                  <Typography fontWeight={750}>{line.targetQuantity} {line.uom}</Typography>
                  <Box>
                    <Typography fontWeight={700}>{line.decision
                      ? t(`inventory.decisions.CONTENT_${line.decision}`, { defaultValue: line.decision })
                      : t("inventory.counting.none")}</Typography>
                    {line.causeCode && <Typography variant="body2">{discrepancyCauseLabels[line.causeCode]}</Typography>}
                    {line.reason && <Typography variant="body2" color="text.secondary">{line.reason}</Typography>}
                    {line.decidedAt && <Typography variant="caption" color="text.secondary">
                      {new Date(line.decidedAt).toLocaleString(locale)} · {line.decidedBy}
                    </Typography>}
                  </Box>
                </Box>)}
              </Stack>
            </Box>
          </>}
          <TextField fullWidth multiline minRows={2} label={t("inventory.dialogs.approvalReason")}
            value={approvalReason} inputProps={{ maxLength: 1000 }}
            helperText={t("inventory.dialogs.approvalHelp")}
            onChange={(event) => setApprovalReason(event.target.value)} />
          {approveCampaign.error && <Alert severity="error">
            {approveCampaign.error instanceof Error ? approveCampaign.error.message : t("inventory.dialogs.approvalError")}
          </Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={approveCampaign.isPending} onClick={() => setCampaignToApprove(null)}>{t("inventory.actions.close")}</Button>
        <Button variant="contained" color="warning"
          disabled={!finalReview.data || approvalReason.trim().length < 3 || approveCampaign.isPending}
          onClick={() => approveCampaign.mutate()}>
          {t("inventory.dialogs.approve")}
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog open={Boolean(contentAdjustmentToConfirm)}
      onClose={() => !remediateContent.isPending && setContentAdjustmentToConfirm(null)} fullWidth maxWidth="sm">
      <DialogTitle>{t("inventory.dialogs.adjustmentTitle")}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="warning">
            {t("inventory.dialogs.adjustmentWarning")}
          </Alert>
          {contentAdjustmentToConfirm && <Box>
            <Typography fontWeight={800}>
              {contentAdjustmentToConfirm.line.huNumber} · {contentAdjustmentToConfirm.line.itemCode}
            </Typography>
            <Typography>
              {t("inventory.dialogs.quantity", { expected: contentAdjustmentToConfirm.line.expectedQuantity, observed: contentAdjustmentToConfirm.line.observedQuantity, uom: contentAdjustmentToConfirm.line.uom })}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t("inventory.investigation.cause", { cause: resolutionCause ? discrepancyCauseLabels[resolutionCause] : t("inventory.result.unspecified") })}
            </Typography>
            <Typography variant="body2" color="text.secondary">{t("inventory.dialogs.reason", { reason: resolutionReason })}</Typography>
          </Box>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={remediateContent.isPending} onClick={() => setContentAdjustmentToConfirm(null)}>{t("inventory.actions.cancel")}</Button>
        <Button variant="contained" color="warning" disabled={!contentAdjustmentToConfirm || remediateContent.isPending}
          onClick={() => contentAdjustmentToConfirm && remediateContent.mutate({
            task: contentAdjustmentToConfirm.task,
            snapshotId: contentAdjustmentToConfirm.line.snapshotId!,
            decision: "STOCK_ADJUSTED"
          }, { onSuccess: () => setContentAdjustmentToConfirm(null) })}>
          {t("inventory.dialogs.prepareAdjustment")}
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog open={Boolean(taskToRecount)}
      onClose={() => !recount.isPending && setTaskToRecount(null)} fullWidth maxWidth="sm">
      <DialogTitle>{t("inventory.dialogs.recountTitle")}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info">
            {t("inventory.dialogs.recountInfo")}
          </Alert>
          {taskToRecount && <Typography>
            {t("inventory.dialogs.recountLocation")} <strong>{taskToRecount.locationCode}</strong>
          </Typography>}
          <TextField fullWidth multiline minRows={2} label={t("inventory.dialogs.recountComment")}
            value={recountComment} inputProps={{ maxLength: 1000 }}
            onChange={(event) => setRecountComment(event.target.value)} />
          {recount.error && <Alert severity="error">
            {recount.error instanceof Error ? recount.error.message : t("inventory.dialogs.recountError")}
          </Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={recount.isPending} onClick={() => setTaskToRecount(null)}>{t("inventory.actions.cancel")}</Button>
        <Button variant="contained" color="warning" disabled={!taskToRecount || recount.isPending}
          onClick={() => taskToRecount && recount.mutate({ task: taskToRecount, comment: recountComment })}>
          {t("inventory.dialogs.confirmRecount")}
        </Button>
      </DialogActions>
    </Dialog>
  </Box>;
}
